/**
 * ChatConversation — message list + compose box for ONE chat session.
 * Rendered by ChatScreen with key={sessionId}: switching sessions remounts
 * this component (clean streaming-state reset + SSE abort). Session
 * selection, the sidebar, and the consent gate live in the shell.
 */
import React, { useState, useRef, useEffect, useMemo, useCallback, useReducer } from 'react';
import {
  View,
  TextInput,
  Pressable,
  Text,
  Image,
  ActivityIndicator,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Alert,
} from 'react-native';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import {
  listMessages,
  postMessage,
  postThesisDismiss,
  postThesisEdit,
  postThesisSave,
  postTargetPriceDismiss,
  postTargetPriceEdit,
  postTargetPriceCompute,
  postTargetPriceSave,
  postProfileSave,
  postProfileEdit,
  postProfileDismiss,
  postImportDismiss,
  postImportUndo,
  postRefreshDismiss,
  stopSession,
  ThesisSaveConflictError,
} from '../../services/chatApi';
import type { ChatMessage, ImportSaveOutcome } from '../../services/chatApi';
import { buildContiguousPages, flattenPages } from './refetchChatPages';
import { streamChatTurn } from '../../services/chatStream';
import { shouldClearPendingMsg, shouldClearThinking } from './shouldClearPending';
import { API_CONFIG } from '../../config/api';
import { getToken } from '../../services/tokenStorage';
import { TopnoteWS } from '../../services/ws';
import { replaySession, replayThesisEntries, saveEnabled } from '../../services/thesisCard';
import {
  acceptKeysForEdit,
  cardValueAt as tpCardValueAt,
  currentStageClient,
  editedKeysForEdit,
  latestCardSnapshotSeq,
  planCardLiveUpdate,
  replayCard as replayTargetPriceCard,
  replayComputed as replayTargetPriceComputed,
  savedReceiptHolds,
  applyEdit as applyTargetPriceEdit,
  normalizeCardSymbol,
  provenanceOf,
  pruneConfirmedForReplacement,
} from '../../services/targetPriceCard';
import type { TargetPriceCard, TargetPriceComputed } from '../../services/targetPriceCard';
import { replayImportEntries, replayImportSession } from '../../services/importPreview';
import {
  replayProfileEntries,
  replayProfileSession,
  profileSaveEnabled,
} from '../../services/profileCard';
import { cancelDocumentImport } from '../../services/api/documentImportApi';
import { getPositions } from '../../services/portfolioApi';
import { KNOWLEDGE_LEVELS } from '../../services/api/personaApi';
import { postPersonaConfirm } from '../../services/chatApi';
import { showSuccess, showError } from '../../utils/alert';
import { useTheme } from '../../contexts/ThemeContext';
import { MessageBubble } from '../../components/chat/MessageBubble';
import { ChatProgressPanel } from '../../components/chat/ChatProgressPanel';
import { QuestionChips } from '../../components/chat/QuestionChips';
import { ImportReviewBar } from '../../components/chat/ImportReviewBar';
import { ReturnChips } from '../../components/chat/ReturnChips';
import { ImportReviewSheet } from '../../components/chat/ImportReviewSheet';
import { ThesisDraftBar } from '../../components/chat/ThesisDraftBar';
import { ThesisDraftSheet } from '../../components/chat/ThesisDraftSheet';
import { ProfileDraftBar } from '../../components/chat/ProfileDraftBar';
import { ProfileDraftSheet } from '../../components/chat/ProfileDraftSheet';
import { TargetPriceDraftBar } from '../../components/chat/TargetPriceDraftBar';
import { TargetPriceDraftSheet } from '../../components/chat/TargetPriceDraftSheet';
import { TargetPriceInputGroup } from '../../components/chat/TargetPriceInputGroup';
import type {
  FlushedRow,
  TargetPriceInputGroupData,
} from '../../components/chat/TargetPriceInputGroup';
import { sanitizeQuestion } from '../../components/chat/question';
import { StaleCoverageNotice } from '../../components/chat/StaleCoverageNotice';
import type { StaleCoverageCard } from '../../components/chat/StaleCoverageNotice';
import type {
  BuildProfileCommandPayload,
  ChatQuestion,
  DiscussItemCommandPayload,
  DiscussSeed,
  ImportCommandPayload,
  ImportFilePayload,
  ImportOptionAction,
  ImportPreview,
  ProfileCard,
  RefreshResearchCommandPayload,
  TargetPriceCommandPayload,
  ThesisCard,
  ThesisCommand,
  ThesisTarget,
} from '../../services/chatStream';
import { appendTrail, TrailEntry } from '../../components/chat/progressTrail';
import { RequestError } from '../../utils/apiError';

// The persona-evolution proposal marker (revamp T25): the PERSONA EVOLUTION
// prompt rule teaches the agent to begin a level-change question's prompt
// with this literal token; the frontend strips it for display and intercepts
// a level-shaped picked answer as the confirm (see sendStreaming). Heuristic
// v1 — a prompt without the marker degrades to an ordinary chat answer.
const PERSONA_QUESTION_MARKER = '[persona]';

function stripPersonaMarker(prompt: string | null): string | null {
  if (prompt && prompt.startsWith(PERSONA_QUESTION_MARKER)) {
    const stripped = prompt.slice(PERSONA_QUESTION_MARKER.length).trim();
    return stripped || null;
  }
  return prompt;
}

export interface ChatConversationProps {
  sessionId: string;
  /** Pre-fills the compose box (or auto-sends with autoSend) on first mount. */
  initialMessage?: string;
  autoSend?: boolean;
  /** Structured seeding turn (spec §3.2 entry A): build_thesis (Feed 复盘 /
   * thesis forms) or build_profile (T24) — auto-sent once on mount, like
   * initialMessage + autoSend. */
  initialCommand?: ThesisCommand | BuildProfileCommandPayload;
  /** A Feed row handed over as *context to discuss* (revamp §6.3, revised
   * 2026-10-09): renders a pending card and holds the discuss_item command
   * until the user sends their first message. Never auto-fires. */
  discussSeed?: DiscussSeed;
}

/** Transcript page size for the chat UI (initial load + each "Load more"). */
const PAGE_SIZE = 20;

/**
 * Follow-up chips auto-send a full gateway turn on tap, so a model-emitted
 * placeholder ("...", "", a lone dash/bullet) must never render as one — a
 * mis-tap would fire an empty, uneditable research turn. Keep only entries that
 * contain at least one letter/digit/CJK char, trimmed and de-duplicated.
 */
function sanitizeSuggestions(list: string[] | null | undefined): string[] {
  if (!Array.isArray(list)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of list) {
    const s = (raw ?? '').trim();
    if (s.length < 2 || !/[\p{L}\p{N}]/u.test(s)) continue;
    const key = s.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

/** One picker-selected file waiting in the thumbnail row. `localUri` is
 * session-local display state and never leaves the device. */
interface PendingImportFile {
  file_data: string;
  file_type: 'image' | 'pdf';
  file_name: string;
  localUri: string;
}

/** One unsaved artifact draft (T16): the (kind, target) projection a return
 * chip restores. Thesis drafts key on their card symbol; import is the
 * session-single slot (target null). `pos` is the winning snapshot's index in
 * the messages array (newest-first ordering); live local state that has not
 * landed in the transcript yet sorts newest via Number.MAX_SAFE_INTEGER. */
interface ArtifactEntry {
  // 'target_price' is DECLARED for the single-slot guards (final-review FIX 2)
  // but no entry is ever pushed with it yet — the target-price draft registers
  // no ReturnChips protocol, it only yields the slot to foreign artifacts.
  kind: 'thesis' | 'import' | 'profile' | 'target_price';
  target: string | null;
  label: string;
  pos: number;
  card?: ThesisCard;
  preview?: ImportPreview;
  profileCard?: ProfileCard;
}

/** Client-side limits mirroring ImportButton (the backend 422s above these). */
const IMPORT_MAX_FILES = 10;
const IMPORT_MAX_BYTES = 10 * 1024 * 1024;
/** Aggregate cap on one import send's base64 payloads. Cloud Run rejects
 * request bodies over 32MB with an opaque transport error, so the send path
 * gates the sum well below it (10 files x 10MB would otherwise total ~133MB of
 * base64). Checked on file_data string length — the exact bytes that go on the
 * wire inside the JSON body. */
const IMPORT_MAX_TOTAL_BASE64 = 30 * 1024 * 1024;

/** The persisted import command turn (spec §3.1): a user message whose
 * metadata.command carries `import_positions` (SHAPE only — the backend strips
 * the base64 payloads, and `metadata.attachment.count` records how many). */
function importCommandOf(message: ChatMessage): Record<string, unknown> | null {
  const meta = message.metadata as Record<string, unknown> | null | undefined;
  if (!meta || typeof meta !== 'object') return null;
  const cmd = meta.command;
  if (
    cmd &&
    typeof cmd === 'object' &&
    !Array.isArray(cmd) &&
    (cmd as Record<string, unknown>).command === 'import_positions'
  ) {
    return cmd as Record<string, unknown>;
  }
  return null;
}

/** The persisted discuss_item turn (revamp §6.3): a user message whose
 * metadata.command carries `discuss_item`; `metadata.discuss_seed` carries the
 * server-composed seed (kind · title · content excerpt). */
function discussCommandOfMessage(message: ChatMessage): Record<string, unknown> | null {
  const meta = message.metadata as Record<string, unknown> | null | undefined;
  if (!meta || typeof meta !== 'object') return null;
  const cmd = meta.command;
  if (
    cmd &&
    typeof cmd === 'object' &&
    !Array.isArray(cmd) &&
    (cmd as Record<string, unknown>).command === 'discuss_item'
  ) {
    return cmd as Record<string, unknown>;
  }
  return null;
}

/** group_id of the newest receipt of *key* (import_saved/import_undone/…),
 * or null. Lets the bar's Undo work on a replayed saved session where no live
 * preview object exists anymore. */
function receiptGroupId(messages: ChatMessage[], key: string): number | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const meta = messages[i]?.metadata as Record<string, unknown> | null | undefined;
    if (!meta || typeof meta !== 'object') continue;
    const receipt = meta[key];
    if (receipt && typeof receipt === 'object' && !Array.isArray(receipt)) {
      const gid = (receipt as Record<string, unknown>).group_id;
      if (typeof gid === 'number') return gid;
    }
  }
  return null;
}

/** The wizard input group riding an assistant row's metadata (tp-wizard Task
 * 7), or null: `{ stage, rows: [{ key, value, rationale? }] }`. */
function inputGroupOf(msg: ChatMessage): TargetPriceInputGroupData | null {
  const meta = msg.metadata as Record<string, unknown> | null | undefined;
  const g = meta?.target_price_input_group;
  if (!g || typeof g !== 'object' || Array.isArray(g)) return null;
  const stage = (g as Record<string, unknown>).stage;
  const rows = (g as Record<string, unknown>).rows;
  if (typeof stage !== 'string' || !Array.isArray(rows)) return null;
  return { stage, rows: rows as TargetPriceInputGroupData['rows'] };
}

/** The research-staleness card riding an assistant row's metadata
 * (research-freshness spec §C.6), or null.
 *
 * The copy rule is that the card states only the four facts the coverage row
 * actually carries, so this copies EXACTLY those keys plus `version` — the
 * row's `rating` / `price_target` / `conviction` have no field to land in.
 * The backend drops them at the source; a client whitelist is the second line
 * of that defence, because a whitelist cannot leak what it does not name. */
function staleCoverageOf(msg: ChatMessage): StaleCoverageCard | null {
  const meta = msg.metadata as Record<string, unknown> | null | undefined;
  const raw = meta?.stale_coverage_card;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const c = raw as Record<string, unknown>;
  // A card with no ticker is no offer: the button it renders would refresh
  // nothing, and the dismissal receipt it writes could never match.
  if (typeof c.ticker !== 'string' || !c.ticker) return null;
  const text = (v: unknown) => (typeof v === 'string' && v ? v : null);
  const card: StaleCoverageCard = {
    ticker: c.ticker,
    name: text(c.name),
    research_date: text(c.research_date),
    current_reporting_period: text(c.current_reporting_period),
    next_catalyst_date: text(c.next_catalyst_date),
    version: typeof c.version === 'number' ? c.version : null,
  };
  // Only the blob's PRESENCE is rendered (as the annotated copy); its
  // reason/at/from_version are server bookkeeping the notice never shows.
  const attempted = c.refresh_attempted;
  if (attempted && typeof attempted === 'object' && !Array.isArray(attempted)) {
    const a = attempted as Record<string, unknown>;
    card.refresh_attempted = {
      at: text(a.at),
      reason: text(a.reason),
      from_version: typeof a.from_version === 'number' ? a.from_version : null,
    };
  }
  return card;
}

/** What one ✕ mutes: the ticker at the coverage version the receipt names.
 * A card with no version keys as `0175.HK|`, which no receipt can carry — the
 * same asymmetry the server documents (a version-less row cannot match a
 * dismissal, so it is offered). */
function staleHideKey(ticker: string, version: number | null): string {
  return `${ticker}|${version ?? ''}`;
}

/** Every (ticker, version) this session has dismissed. Mirrors
 * `run_flow._refresh_dismissed_for` in being PRESENCE-based, not ordered: the
 * server's probe asks only whether the transcript holds such a receipt, which
 * is what lets a later publish — a new version — re-arm the offer instead of
 * one tap muting the ticker for the session. */
function refreshDismissals(messages: ChatMessage[]): Set<string> {
  const keys = new Set<string>();
  for (const m of messages) {
    const meta = m.metadata as Record<string, unknown> | null | undefined;
    const raw = meta?.refresh_dismissed;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue;
    const r = raw as Record<string, unknown>;
    if (typeof r.ticker === 'string' && typeof r.version === 'number') {
      keys.add(staleHideKey(r.ticker, r.version));
    }
  }
  return keys;
}

/** The bubble accepts' wire fields (tp-wizard Task 11): driver keys fold into
 * the scenario dict seeded from the card (`{ bull: {...card.bull, wacc: value} }`),
 * probability keys patch bare. `value` overrides the card's own number (the
 * free-input commit path); the card supplies the sibling drivers.
 *
 * The sibling spread stopped being load-bearing when apply_edit began merging
 * scenario patches per driver (2026-10-31 staging, 0100.HK) — a partial dict no
 * longer erases its siblings. It stays as the sheet's shared full-dict wire
 * shape, which is inert under the merge. */
function tpFieldsForRows(
  rows: Array<{ key: string; value: number | null }>,
  card: TargetPriceCard
): Record<string, unknown> {
  const cardDict = card as unknown as Record<string, unknown>;
  const fields: Record<string, unknown> = {};
  const scenarioPatches = new Map<string, Record<string, unknown>>();
  for (const { key, value } of rows) {
    const dot = key.indexOf('.');
    if (dot === -1) {
      fields[key] = value;
      continue;
    }
    const sc = key.slice(0, dot);
    let patch = scenarioPatches.get(sc);
    if (!patch) {
      const prev = cardDict[sc];
      patch = { ...((prev && typeof prev === 'object' ? prev : {}) as Record<string, unknown>) };
      scenarioPatches.set(sc, patch);
    }
    patch![key.slice(dot + 1)] = value;
  }
  for (const [sc, patch] of scenarioPatches) fields[sc] = patch;
  return fields;
}

/** The card a sheet footer act must POST (2026-10-29 review I-2): live state
 * folded with the rows the sheet had to flush to fire. Same-tick rule as the
 * bubble's confirm — the flush's own commit POSTs are still in flight and their
 * optimistic setState is invisible to this closure, so reading state alone
 * would compute/save the AI's suggestion over what the user just typed. */
function tpCardWithFlush(card: TargetPriceCard, flushed: FlushedRow[]): TargetPriceCard {
  return flushed.length ? applyTargetPriceEdit(card, tpFieldsForRows(flushed, card)) : card;
}

/** The user-side import turn (spec §3.1): while the session is alive the
 * bubble shows the picked gallery thumbnails; after a reload (URIs dead) it
 * degrades to the 📷 placeholder. Same user-aligned bubble shape as
 * MessageBubble — this branch exists because MessageBubble collapses every
 * metadata.command message into a muted line. */
function ImportPhotoBubble({ message, uris }: { message: ChatMessage; uris?: string[] }) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const meta = (message.metadata ?? null) as Record<string, any> | null;
  const count = Math.min(
    IMPORT_MAX_FILES,
    Math.max(1, Number(meta?.attachment?.count) || uris?.length || 1)
  );
  return (
    <View
      testID="import-photo-bubble"
      style={{
        flexDirection: 'row',
        justifyContent: 'flex-end',
        marginVertical: 4,
        paddingHorizontal: 12,
      }}
    >
      <View
        style={{
          maxWidth: '80%',
          padding: 10,
          borderRadius: 12,
          backgroundColor: theme.colors.primary,
          flexShrink: 1,
          minWidth: 0,
        }}
      >
        {uris && uris.length > 0 ? (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
            {uris.map((uri, i) => (
              <Image
                key={`${i}-${uri}`}
                source={{ uri }}
                style={{ width: 64, height: 64, borderRadius: 8 }}
              />
            ))}
          </View>
        ) : (
          <Text style={{ color: theme.colors.textInverse, fontSize: theme.typography.fontSizeMd }}>
            {`${'📷'.repeat(count)} ${t('chat.import.placeholder', {
              defaultValue: 'Screenshot (processed)',
            })}`}
          </Text>
        )}
        {message.content && message.content !== '📷' ? (
          <Text
            style={{
              color: theme.colors.textInverse,
              fontSize: theme.typography.fontSizeMd,
              marginTop: uris?.length ? 6 : 0,
            }}
          >
            {message.content}
          </Text>
        ) : null}
      </View>
    </View>
  );
}

/** The persisted discuss_item turn (revamp §6.3): a compact one-liner naming
 * the Feed item under discussion — the title comes from the server-composed
 * `discuss_seed`, never the generic muted command collapse (whose content is
 * just the sent label). A seed without a title degrades to the bare label. */
function DiscussBubble({ message }: { message: ChatMessage }) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const meta = (message.metadata ?? null) as Record<string, any> | null;
  const rawTitle = meta?.discuss_seed?.title;
  const title = typeof rawTitle === 'string' && rawTitle.trim() ? rawTitle.trim() : null;
  return (
    <View testID="discuss-command-bubble" style={{ paddingHorizontal: 12, marginVertical: 2 }}>
      <Text
        style={{
          color: theme.colors.textTertiary,
          fontSize: theme.typography.fontSizeSm,
          textAlign: 'right',
        }}
      >
        {title
          ? t('chat.discussingTitle', { title, defaultValue: '💬 Discussing: {{title}}' })
          : t('chat.discussing', { defaultValue: '💬 Discussing' })}
      </Text>
    </View>
  );
}

export function ChatConversation({
  sessionId,
  initialMessage,
  autoSend,
  initialCommand,
  discussSeed,
}: ChatConversationProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigation = useNavigation<any>();

  // ── Message state ──
  const [inputText, setInputText] = useState('');
  // One-shot latch for the deferred Feed discuss seed (revamp §6.3, revised
  // 2026-10-09): flips true the moment the user's first send consumes the held
  // discuss_item command, so the pending card hides and later sends never
  // re-attach it — even before the cleared route param propagates.
  const [discussDone, setDiscussDone] = useState(false);
  // The `+` command menu (spec §3.2 entry B): closed by default, toggled by
  // the compose-row `+` button, dismissed by selection or backdrop tap.
  const [menuOpen, setMenuOpen] = useState(false);
  // ── Target-price picker in the `+` menu (T15 entry point 1) ──
  // The backend thesis hard-gate refuses a null-symbol `target_price`
  // command, so the menu entry must resolve a concrete symbol BEFORE
  // sending: tapping it swaps the popover into a position picker fed by
  // getPositions() on tap (lightest existing pattern — no new modal, the
  // popover's own click-away doctrine keeps working). No positions (or a
  // failed fetch) renders a disabled hint, never a dead send.
  const [tpPickerOpen, setTpPickerOpen] = useState(false);
  const [tpPickerLoading, setTpPickerLoading] = useState(false);
  // Deduped, non-empty symbols of the user's positions; null while loading.
  const [tpPickerSymbols, setTpPickerSymbols] = useState<string[] | null>(null);
  useEffect(() => {
    // The menu (or its backdrop / click-away) closing also folds the picker
    // back, so a reopen shows the command list again.
    if (!menuOpen) {
      setTpPickerOpen(false);
      setTpPickerSymbols(null);
    }
  }, [menuOpen]);
  const [pendingMsg, setPendingMsg] = useState<string | null>(null);
  const [isThinking, setIsThinking] = useState(false);
  const [streamingText, setStreamingText] = useState<string | null>(null);
  // One object, not two fields: sendStreaming is a useCallback that does not
  // depend on this state, so every update must go through the functional form.
  // `total` lives outside the trail because the trail is capped and `total` is not.
  const [progress, setProgress] = useState<{ trail: TrailEntry[]; total: number }>({
    trail: [],
    total: 0,
  });
  const [streamStartedAt, setStreamStartedAt] = useState<number>(() => Date.now());
  const [suggestions, setSuggestions] = useState<string[]>([]);
  // The active question replaces the suggestions row for its turn: an answer
  // the agent is waiting on, not passive "you could ask" prompts.
  const [question, setQuestion] = useState<ChatQuestion | null>(null);
  // Mirror for sendStreaming's useCallback (it must not depend on question
  // state — same doctrine as progress above): the persona-evolution
  // interception (T25) reads it at tap time, chips render only while no turn
  // is in flight, so the ref is always current when it matters.
  const questionRef = useRef<ChatQuestion | null>(null);
  useEffect(() => {
    questionRef.current = question;
  }, [question]);
  // The live thesis card (spec §4): projected from the transcript on load,
  // updated by `thesis_card` SSE events, edited via the sheet, saved on
  // confirm. `saved` flips the draft bar to its check state; `saving` drives
  // the sheet's save button; the sheet pops only at the ACT-ON-FINAL edge
  // (2026-10-31 doctrine — saveEnabled, once per draft lifecycle).
  const [thesisCard, setThesisCard] = useState<ThesisCard | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [thesisSaved, setThesisSaved] = useState(false);
  const [savingThesis, setSavingThesis] = useState(false);
  // One-shot per completion (incident #6, 2026-10-04): when a card update
  // makes the draft saveable (duration + statement — the sheet's enabled Save
  // button IS the prompt; nothing else told the user to save), the sheet
  // auto-opens. The ref resets while the card is incomplete, so a
  // revise-then-recomplete cycle re-prompts.
  const completionPromptedRef = useRef(false);
  // Doctrine guards (2026-10-31): once-per-DRAFT-LIFECYCLE completion prompts.
  // Reset whenever the draft goes incomplete again — un-complete → re-complete
  // re-prompts; a mid-build draft never pops.
  const profileCompletionPromptedRef = useRef(false);
  const targetPriceCompletionPromptedRef = useRef(false);
  // Set when the user dismisses the draft bar; the transcript replay below
  // must not resurrect a dismissed card on the next refetch. Cleared when a
  // fresh card event lands (a new card re-arms the bar).
  const thesisDismissedRef = useRef(false);
  // The live profile card (revamp T24): the thesis card's simplified twin —
  // three chip dimensions, no free-typed fields, one save endpoint writing
  // both stores. `saved` flips the bar to its ✓ receipt; dismissal is LOCAL
  // only (no server receipt — a reload replays the draft until profile_saved).
  const [profileCard, setProfileCard] = useState<ProfileCard | null>(null);
  const [profileSheetOpen, setProfileSheetOpen] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  // Local-hide guard (same doctrine as thesisDismissedRef): without it the
  // replay effect below resurrects the dismissed draft on the next refetch.
  // Cleared when a fresh profile_card event lands (a new draft re-arms).
  const profileDismissedRef = useRef(false);
  // The live target-price card (Part 2, Task 12): the thesis card's twin on
  // the targetPriceCard domain. `confirmed` is the client-only engagement map
  // the compute gate reads (computeEnabled) and the save provenance is derived
  // from (provenanceOf) — a driver counts only once the user actively
  // confirmed/edited it. `computed` exists ONLY after a compute round-trip
  // (spec §12); a draft change invalidates it. `saved` flips the bar to its ✓
  // receipt in-session; on reload the flag DERIVES from the transcript
  // (savedReceiptHolds — fix-wave 3): a card-carrying saved receipt reopens
  // the bar in its ✓ form with edits/save gated, while a card-LESS (legacy)
  // receipt still kills the draft in replayCard entirely.
  const [targetPriceCard, setTargetPriceCard] = useState<TargetPriceCard | null>(null);
  const [targetPriceSheetOpen, setTargetPriceSheetOpen] = useState(false);
  const [targetPriceConfirmed, setTargetPriceConfirmed] = useState<Record<string, boolean>>({});
  const [targetPriceSaved, setTargetPriceSaved] = useState(false);
  const [targetPriceComputed, setTargetPriceComputed] = useState<TargetPriceComputed | null>(null);
  const [computingTargetPrice, setComputingTargetPrice] = useState(false);
  const [savingTargetPrice, setSavingTargetPrice] = useState(false);
  // M4 (2026-10-31 staging, seq 26/28 — a 132 ms double-tap): the wizard
  // group's confirm POST is in flight, so its button renders spent. The stamp
  // that keeps it spent afterwards only appears once refetchMessages lands,
  // 20–40 s into the agent turn; without this flag the button re-arms for the
  // whole window and every re-tap is another identical target_price_edit.
  const [confirmingTargetPriceGroup, setConfirmingTargetPriceGroup] = useState(false);
  // Live mirror of the card for callbacks that run across awaits/closures
  // (the SSE onTargetPriceCard staleness check) — sendStreaming must not
  // depend on card state, same doctrine as questionRef.
  const targetPriceCardRef = useRef<TargetPriceCard | null>(null);
  // Dismissal guard (thesis doctrine): the receipt is a UI-only marker the
  // transcript replay ignores by design, so the reload path scans for it in
  // the seed effect below and this ref kills the bar for the rest of the mount.
  const targetPriceDismissedRef = useRef(false);
  // Seq of the last target_price_card snapshot applied to the sheet state
  // (seed or live-update); lets the transcript effect detect NEWER snapshots.
  const appliedCardSeqRef = useRef<number | null>(null);
  useEffect(() => {
    targetPriceCardRef.current = targetPriceCard;
  }, [targetPriceCard]);
  // ── Active artifact slot (T16 return chips) ──
  // One bar area, several unsaved drafts: this is the (kind,target) shown in
  // the bar + sheet. Null = follow the newest replayed entry; a tap on a
  // return chip pins the displaced draft. A pinned override that the next
  // replay terminalizes (its receipt landed) falls back to the newest entry.
  const [activeOverride, setActiveOverride] = useState<{
    kind: 'thesis' | 'import' | 'profile' | 'target_price';
    target: string | null;
  } | null>(null);
  // ── Import flow state (spec §3.1–§3.5) ──
  // Picker output docked above the compose row until Send. `localUri` is
  // display-only; only {file_data, file_type, file_name} leaves the device.
  const [pendingFiles, setPendingFiles] = useState<PendingImportFile[]>([]);
  // Extraction in flight (`import_pending` SSE): the reading row + Cancel.
  const [importPending, setImportPending] = useState<{ count: number; importIds: number[] } | null>(
    null
  );
  // The review sheet (transcript-as-state, spec §4.5): projected from the
  // transcript on load (replay) or driven live by `import_preview`; every
  // lifecycle transition is applied locally (see the replay effect).
  const [importState, setImportState] = useState<
    'active' | 'saved' | 'undone' | 'none' | 'discarded'
  >('none');
  const [importPreviewState, setImportPreviewState] = useState<ImportPreview | null>(null);
  // Set on save (live result or replayed receipt): the sheet's footer becomes
  // [Undo] and the bar shows the ✓ receipt with Undo.
  const [importSavedResults, setImportSavedResults] = useState<{ group_id: number } | null>(null);
  // The import sheet's visibility — independent of the thesis sheet. Preview
  // state and this flag always change in the SAME handler (batched), so the
  // sheet can never open against a stale/null preview.
  const [importSheetOpen, setImportSheetOpen] = useState(false);
  // One-shot sheet auto-open on the first `import_preview` with rows (per
  // mount — the import flow keeps its own auto-open; the 2026-10-31 doctrine
  // flip covers the thesis/profile/target-price builders only).
  const importAutoOpenedRef = useRef(false);
  // Flips once the bar/sheet has LIVE state (a replay seed, a live event, or
  // any local transition). From then on the transcript replay never runs
  // again this mount — local state owns the bar, and a WS refetch (e.g. the
  // receipt landing after a save) cannot clobber it.
  const importLiveRef = useRef(false);
  // Local gallery URIs for import photo bubbles, keyed by the PERSISTED user
  // message id. The picker's URIs die with this component (a session reload
  // renders the 📷 placeholder), and the base64 payload is never stored here.
  const localUrisRef = useRef<Record<string, string[]>>({});
  // In-flight staging: set at send time, bound to the message id once the
  // refetched transcript carries the persisted copy.
  const unboundImportUrisRef = useRef<string[] | null>(null);
  // The import message the staging was last bound to. A WS/reconnect catch-up
  // landing during the multi-second upload window re-delivers the PREVIOUS
  // import turn — binding to it would clear the staging and leave the new
  // send a permanent placeholder, so the binding effect skips it instead.
  const lastBoundImportMsgIdRef = useRef<string | null>(null);
  // Live-view mirror of the preview for callbacks that decide ACROSS an await
  // (the cancel race): the state captured at render time is stale by the time
  // the awaited dismiss resolves.
  const importPreviewRef = useRef<ImportPreview | null>(null);
  // Render tick: binding a URI entry happens inside an effect (after commit),
  // so the bubble must repaint once to swap the placeholder for thumbnails.
  const [, forceUrisRender] = useReducer((n: number) => n + 1, 0);
  const sendingRef = useRef(false);
  // Sends made while a turn is in flight (spec §6.5 queue): the composed
  // message goes to the server's queue via the non-stream POST and shows in a
  // LOCAL list (the "还有 N 条待回复" hint) until the transcript catches up.
  // `key` starts random and is rebound to the persisted message id once the
  // POST answers — that id is what the reconcile effect matches on.
  const [queuedMsgs, setQueuedMsgs] = useState<{ key: string; content: string }[]>([]);
  // Same doctrine as sendingRef: a synchronous re-entry gate so a double-tap
  // on Send cannot queue the same composed text twice.
  const queueingRef = useRef(false);
  const scrollRef = useRef<ScrollView>(null);
  // Aborted on unmount: session switches remount this component (key change),
  // and the old display pipe must stop. The server-side turn keeps running
  // and persists durably — returning to the session refetches the reply.
  const abortRef = useRef<AbortController | null>(null);

  // ── Messages query (paginated: most recent page first, older on demand) ──
  // Long sessions must not load the whole transcript on open — the first page
  // is the latest PAGE_SIZE messages; "Load more history" at the top of the
  // list pages further back via the oldest loaded seq as the cursor.
  const {
    data: messagePages,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['chat-messages', sessionId],
    queryFn: ({ pageParam }) =>
      listMessages(sessionId!, { limit: PAGE_SIZE, beforeSeq: pageParam ?? undefined }),
    initialPageParam: null as number | null,
    getNextPageParam: (lastPage) =>
      lastPage.has_more && lastPage.messages.length > 0 && lastPage.messages[0].seq != null
        ? lastPage.messages[0].seq!
        : null,
    enabled: !!sessionId,
  });
  const messages = useMemo(() => flattenPages(messagePages), [messagePages]);

  // True once the transcript carries any structured command turn (the discuss /
  // thesis / import / profile bubble). A returning session already shows the
  // server-seeded discuss bubble, so the pending card must not reappear.
  const hasCommandBubble = messages.some((m) => {
    const meta = m.metadata;
    return (
      meta != null && typeof meta === 'object' && (meta as Record<string, unknown>).command != null
    );
  });
  // The deferred Feed discuss card is live only while a seed is present, the
  // user hasn't sent their first message yet, and the transcript has no command
  // bubble to supersede it.
  const showDiscussSeed = discussSeed != null && !discussDone && !hasCommandBubble;

  // Refresh the transcript for everything that lands (polling, stream
  // lifecycle, WS push). NOT plain invalidateQueries: React Query refetches
  // infinite pages with their ORIGINAL pageParams, so once older pages are
  // loaded a new message re-anchors page 1 to the newest window while older
  // pages keep frozen cursors — the transcript grows a seq hole. We rebuild
  // the loaded depth with fresh, derived cursors instead (single page → the
  // plain invalidation is already correct and cheaper).
  const refetchMessages = useCallback(async () => {
    if (!sessionId) return;
    const depth =
      qc.getQueryData<{ pages: unknown[] }>(['chat-messages', sessionId])?.pages.length ?? 0;
    if (depth <= 1) {
      qc.invalidateQueries({ queryKey: ['chat-messages', sessionId] });
      return;
    }
    const state = await buildContiguousPages(
      (beforeSeq) => listMessages(sessionId, { limit: PAGE_SIZE, beforeSeq }),
      depth
    );
    qc.setQueryData(['chat-messages', sessionId], state);
  }, [sessionId, qc]);

  // ── Artifact entries over the transcript (T16 return chips) ──
  // replayThesisEntries/replayImportEntries project EVERY unsaved draft out
  // of chat_messages.metadata; the live local state (the card/preview this
  // mount is driving) overrides its replayed twin, so an un-persisted SSE
  // snapshot and its folded edits render exactly once. Entries merge
  // newest-first by the winning snapshot's transcript position; live-only
  // state (not yet persisted) sorts newest.
  const thesisEntries = useMemo(() => replayThesisEntries(messages), [messages]);
  const importEntry = useMemo(() => replayImportEntries(messages), [messages]);
  const profileEntry = useMemo(() => replayProfileEntries(messages), [messages]);
  // The wizard's confirmed stage keys and typed subset come from the SERVER's
  // fold (B3, 2026-10-31 staging 0100.HK), riding the newest transcript page.
  // The server folds the wizard state and ships it on the NEWEST transcript
  // page (`target_price_state`). This screen used to replay both sets from
  // `messages` — which is only the pages it has fetched, PAGE_SIZE = 20 each,
  // against the server's 200-row window. A mid-wizard reload therefore
  // reported a stage the server had already advanced past, and Compute stayed
  // disabled on a build that was in fact complete. Page 0 is always the newest
  // window (buildContiguousPages fetches it cursor-less), so the fold can
  // never be staler than the transcript sitting beside it.
  const targetPriceWizardState = messagePages?.pages[0]?.target_price_state;
  // Threaded as `undefined`, never as an empty Set, when the server shipped no
  // fold (review #1): both consumers treat absent as the LOOSE side — the sheet
  // arms Compute on value presence alone and the input group reads its stage as
  // un-stamped, so a backend without B3, a fold the server degraded away after
  // a failed probe, or a malformed payload leaves the wizard usable instead of
  // permanently disabled. A present-but-empty `{confirmed: []}` is the real
  // "draft exists, nothing confirmed" and still gates strictly.
  const targetPriceConfirmedKeys = useMemo(
    () => (targetPriceWizardState ? new Set(targetPriceWizardState.confirmed) : undefined),
    [targetPriceWizardState]
  );
  const targetPriceEditedKeys = useMemo(
    () => new Set(targetPriceWizardState?.edited ?? []),
    [targetPriceWizardState]
  );
  const entries = useMemo<ArtifactEntry[]>(() => {
    const list: ArtifactEntry[] = [];
    const thesisLabel = (symbol: string) =>
      t('chat.entryThesis', { symbol, defaultValue: '{{symbol}} thesis' });
    const importLabel = t('chat.menu.import', { defaultValue: 'Import positions / trades' });
    const profileLabel = t('chat.entryProfile', { defaultValue: 'Build profile' });
    const liveThesis = thesisCard && !thesisSaved ? thesisCard : null;
    for (const e of thesisEntries) {
      if (liveThesis && e.symbol === liveThesis.symbol) {
        list.push({
          kind: 'thesis',
          target: e.symbol,
          label: thesisLabel(e.symbol),
          pos: e.pos,
          card: liveThesis,
        });
      } else if (!(thesisSaved && thesisCard && e.symbol === thesisCard.symbol)) {
        // Not live: keep the replayed snapshot — unless this mount just
        // finalized that symbol (thesisSaved): the saved receipt bar owns it
        // until the transcript receipt closes the draft for good.
        list.push({
          kind: 'thesis',
          target: e.symbol,
          label: thesisLabel(e.symbol),
          pos: e.pos,
          card: e.card,
        });
      }
    }
    if (liveThesis && !thesisEntries.some((e) => e.symbol === liveThesis.symbol)) {
      list.push({
        kind: 'thesis',
        target: liveThesis.symbol,
        label: thesisLabel(liveThesis.symbol),
        pos: Number.MAX_SAFE_INTEGER,
        card: liveThesis,
      });
    }
    const liveImport = importState === 'active' ? importPreviewState : null;
    if (importEntry.length > 0) {
      // A terminal LOCAL state (saved/undone/discarded this mount) owns the
      // slot until the receipt lands in the transcript — the replayed draft
      // must not chip while the receipt bar shows.
      if (!(importLiveRef.current && importState !== 'active')) {
        const e = importEntry[0];
        list.push({
          kind: 'import',
          target: null,
          label: importLabel,
          pos: e.pos,
          preview:
            liveImport && liveImport.group_id === e.preview.group_id ? liveImport : e.preview,
        });
      }
    } else if (liveImport) {
      list.push({
        kind: 'import',
        target: null,
        label: importLabel,
        pos: Number.MAX_SAFE_INTEGER,
        preview: liveImport,
      });
    }
    // Profile slot (T24): single (kind,target) like import. The live card
    // overrides its replayed twin; a just-saved draft drops out until the
    // transcript receipt closes it for good (mirror thesis).
    const liveProfile = profileCard && !profileSaved ? profileCard : null;
    if (profileEntry.length > 0) {
      if (!(profileSaved && profileCard)) {
        const e = profileEntry[0];
        list.push({
          kind: 'profile',
          target: null,
          label: profileLabel,
          pos: e.pos,
          profileCard: liveProfile ?? e.card,
        });
      }
    } else if (liveProfile) {
      list.push({
        kind: 'profile',
        target: null,
        label: profileLabel,
        pos: Number.MAX_SAFE_INTEGER,
        profileCard: liveProfile,
      });
    }
    // Deps: `messages` flows in via thesisEntries/importEntry/profileEntry
    // (all re-memo on it), so listing it here would only be redundant.
    return list.sort((a, b) => b.pos - a.pos);
  }, [
    thesisEntries,
    importEntry,
    profileEntry,
    thesisCard,
    thesisSaved,
    importState,
    importPreviewState,
    profileCard,
    profileSaved,
    t,
  ]);

  // The entry the bar+sheet show: the pinned override while it survives, else
  // the newest entry. A pinned override whose draft got terminalized (its
  // receipt landed) vanishes from entries and falls back to newest here.
  const activeEntry = useMemo(() => {
    if (entries.length === 0) return null;
    if (activeOverride) {
      const found = entries.find(
        (e) => e.kind === activeOverride.kind && e.target === activeOverride.target
      );
      if (found) return found;
    }
    return entries[0];
  }, [entries, activeOverride]);

  useEffect(() => {
    if (
      activeOverride &&
      !entries.some((e) => e.kind === activeOverride.kind && e.target === activeOverride.target)
    ) {
      setActiveOverride(null);
    }
  }, [entries, activeOverride]);

  const activeIsThesis = activeEntry?.kind === 'thesis';
  const activeIsImport = activeEntry?.kind === 'import';
  const activeIsProfile = activeEntry?.kind === 'profile';
  const activeIsTargetPrice = activeEntry?.kind === 'target_price';
  // Single bar area (one draft on screen at a time): the thesis bar renders
  // when the active slot is the thesis draft — or its saved ✓ receipt, which
  // always renders regardless of which entry is active.
  const thesisBarVisible = thesisCard != null && (thesisSaved || activeIsThesis);
  const importEntryBarVisible = importState === 'active' && activeIsImport;
  const profileBarVisible = profileCard != null && (profileSaved || activeIsProfile);
  // Target-price bar (final-review FIX 2): same single-artifact-slot doctrine
  // as the thesis/profile bars. The draft registers no entries-list slot yet
  // (no ReturnChips protocol for it), so activeEntry is never its kind — the
  // bar yields the slot while ANY foreign artifact draft is active, and shows
  // when the slot is free or its own saved ✓ receipt owns it (a receipt bar
  // renders regardless, thesis doctrine).
  const targetPriceBarVisible =
    targetPriceCard != null && (targetPriceSaved || activeIsTargetPrice || activeEntry == null);
  const chipEntries = useMemo(
    () =>
      entries.filter((e) => {
        if (e.kind === 'thesis') {
          // The bar's own draft (or its just-saved receipt) never chips.
          if (thesisBarVisible && thesisCard && e.target === thesisCard.symbol) return false;
        } else if (e.kind === 'profile') {
          // Same single-slot doctrine: the profile bar owns its draft.
          if (profileBarVisible) return false;
        } else if (importEntryBarVisible) {
          return false;
        }
        return true;
      }),
    [entries, thesisBarVisible, importEntryBarVisible, thesisCard, profileBarVisible]
  );

  // ── Research job tracking ──
  // A research ack has job_id set but no citations. The completion message has
  // citations (wiki links). A job is "resolved" when:
  //   1. A completion message (with citations) exists for the same job_id
  //   2. OR 2+ messages exist for the same job_id (ack + completion/failure)
  //   3. OR the ack is older than 30 min (timeout fallback)
  const { pendingJobCount, resolvedJobIds } = useMemo(() => {
    const jobMsgCount = new Map<string, number>();
    const jobHasCitations = new Set<string>();
    const jobCreatedAt = new Map<string, number>(); // ms timestamp
    for (const m of messages) {
      if (m.role === 'assistant' && m.job_id) {
        jobMsgCount.set(m.job_id, (jobMsgCount.get(m.job_id) || 0) + 1);
        if (m.citations && m.citations.length > 0) {
          jobHasCitations.add(m.job_id);
        }
        const ts = new Date(m.created_at).getTime();
        if (!jobCreatedAt.has(m.job_id) || ts < (jobCreatedAt.get(m.job_id) ?? 0)) {
          jobCreatedAt.set(m.job_id, ts);
        }
      }
    }
    const now = Date.now();
    const TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
    const resolved = new Set<string>();
    for (const [jobId, count] of jobMsgCount) {
      if (jobHasCitations.has(jobId))
        resolved.add(jobId); // has completion
      else if (count > 1)
        resolved.add(jobId); // ack + failure/other
      else {
        const age = now - (jobCreatedAt.get(jobId) ?? 0);
        if (age > TIMEOUT_MS) resolved.add(jobId); // timed out
      }
    }
    const pending = [...jobMsgCount.keys()].filter((id) => !resolved.has(id)).length;
    return { pendingJobCount: pending, resolvedJobIds: resolved };
  }, [messages]);

  // ── Recover from page refresh during streaming ──
  // If the last message is from the user with no assistant reply yet,
  // the backend is still processing. Show thinking indicator and poll
  // until the response arrives via WebSocket or polling.
  useEffect(() => {
    if (isThinking || pendingMsg || sendingRef.current) return; // already tracking
    if (!messages || messages.length === 0) return;
    const last = messages[messages.length - 1];
    if (last.role !== 'user') return;
    // Only recover if the orphan is recent (< 5 min)
    const age = Date.now() - new Date(last.created_at).getTime();
    if (age > 5 * 60 * 1000) return;
    setIsThinking(true);
  }, [messages, isThinking, pendingMsg]);

  // Stream-death recovery: if the SSE pipe died mid-turn (proxy reap, network
  // blip) the server-side turn still finishes and the reply lands via
  // WebSocket/poll. An assistant reply newer than this turn's start IS the
  // completion signal — clear the spinner the moment it appears, not at the
  // 5-minute timeout.
  useEffect(() => {
    if (shouldClearThinking(messages, isThinking, streamStartedAt)) {
      setIsThinking(false);
      setStreamingText(null);
      setProgress({ trail: [], total: 0 });
      sendingRef.current = false;
    }
  }, [messages, isThinking, streamStartedAt]);

  // Clear the optimistic user bubble once the server has persisted our
  // message (it then renders via `messages`). We do NOT wait for the assistant
  // reply: doing so left both copies on screen for the whole gateway turn,
  // showing the user's message twice. No gap — the server copy takes over the
  // instant this clears. isThinking is driven by the stream lifecycle
  // (onDone/onError/timeout), not here.
  useEffect(() => {
    if (shouldClearPendingMsg(messages, pendingMsg)) {
      setPendingMsg(null);
    }
  }, [messages, pendingMsg]);

  // Queue reconciliation — server truth wins: once the refetched transcript
  // (WS push, poll, stream completion) carries a queued message's persisted
  // copy, the local entry leaves the queue and the hint count follows.
  useEffect(() => {
    if (queuedMsgs.length === 0) return;
    const remaining = queuedMsgs.filter((q) => !messages.some((m) => m.id === q.key));
    if (remaining.length !== queuedMsgs.length) setQueuedMsgs(remaining);
  }, [messages, queuedMsgs]);

  // Surface suggestions from the last assistant message on load (persisted
  // across page refreshes). Live suggestions arrive via the SSE stream and
  // setSuggestions above; this catches the case where the user reloads the
  // page and the transcript is re-fetched from the DB. A persisted question
  // takes precedence — its chips replace the suggestions row.
  useEffect(() => {
    if (suggestions.length > 0 || question || sendingRef.current) return;
    const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
    if (lastAssistant?.question) {
      const q = sanitizeQuestion(lastAssistant.question);
      if (q) {
        setQuestion(q);
        return;
      }
    }
    if (lastAssistant?.suggestions?.length) {
      setSuggestions(sanitizeSuggestions(lastAssistant.suggestions));
    }
  }, [messages, suggestions.length, question]);

  // Timeout fallback — prevent permanent "Thinking..." (5 min for T3 research)
  useEffect(() => {
    if (!isThinking) return;
    // Also release sendingRef: without it a stream that never calls
    // onDone/onError would brick the compose box (handleSend no-ops while
    // sendingRef is set) even after the spinner is gone.
    const timer = setTimeout(() => {
      sendingRef.current = false;
      setIsThinking(false);
    }, 300_000);
    return () => clearTimeout(timer);
  }, [isThinking]);

  // Polling fallback while thinking OR while T3 research jobs are pending
  useEffect(() => {
    if ((!isThinking && pendingJobCount === 0) || !sessionId) return;
    const intervalMs = isThinking ? 3000 : 15000; // slower poll for pending jobs
    const interval = setInterval(() => {
      refetchMessages();
    }, intervalMs);
    return () => clearInterval(interval);
  }, [isThinking, pendingJobCount, sessionId, refetchMessages]);

  // A 428 means consent was revoked mid-session: invalidate the shell's
  // consent query so it re-renders the ConsentScreen gate.
  const refetchConsent = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['chat-consent'] });
  }, [qc]);

  // ── Thesis card replay on transcript load (spec §4.1) ──
  // Runs only before any live card state exists: once a card is live (or the
  // user dismissed the bar), the projection must not overwrite it. The saved /
  // dismissed flags ride along: a session whose newest lifecycle event is a
  // `thesis_saved` or `thesis_dismissed` receipt reopens with NO bar at all
  // (a finished or dismissed draft is not re-armed on load). An un-dismissed
  // card seeds the bar; the sheet pops only when the replayed draft is already
  // ACT-ON-FINAL (saveEnabled — 2026-10-31 doctrine: the sheet that opens IS
  // the save prompt; an incomplete draft is bar-only until the live
  // completion edge fires). Accepted limitation: this replays the LOADED
  // transcript window only — no extra pages are fetched for replay (the
  // snapshot is recent in practice).
  useEffect(() => {
    if (thesisCard || thesisDismissedRef.current || sendingRef.current) return;
    // T16: the active slot belongs to another artifact (e.g. a live import) —
    // do not seed the thesis bar/sheet over it. The displaced drafts still
    // chip via the entries memo; restoring a chip seeds from the entry, not
    // through this replay.
    if (activeEntry && activeEntry.kind !== 'thesis') return;
    const { card, saved, dismissed } = replaySession(messages);
    if (!card || dismissed || saved) {
      if (dismissed) thesisDismissedRef.current = true;
      return;
    }
    setThesisCard(card);
    // DOCTRINE: pop iff the replayed draft is already ACT-ON-FINAL — the
    // sheet that opens IS the save prompt. An incomplete draft seeds the
    // bar only; the live completion edge prompts when it completes.
    if (saveEnabled(card)) {
      completionPromptedRef.current = true;
      setSheetOpen(true);
    }
  }, [messages, thesisCard, activeEntry]);

  // ── Profile card replay on transcript load (revamp T24) ──
  // Same doctrine as the thesis replay: runs only before any live card state;
  // a saved receipt means NO bar on load; the sheet pops only when the folded
  // draft is already ACT-ON-FINAL (profileSaveEnabled — all three dimensions
  // decided); an incomplete draft seeds the bar only. A dismissed receipt
  // (2026-10-31 §3.B — the ✕ now persists it) marks the ref so the draft
  // stays dead until a fresh profile_card lands.
  useEffect(() => {
    if (profileCard || profileDismissedRef.current || sendingRef.current) return;
    if (activeEntry && activeEntry.kind !== 'profile') return;
    const { card, saved, dismissed } = replayProfileSession(messages);
    if (!card || dismissed || saved) {
      if (dismissed) profileDismissedRef.current = true;
      return;
    }
    setProfileCard(card);
    // DOCTRINE: pop iff the profile fold (agent snapshot + user edits) is
    // complete — all three dimensions decided. Incomplete ⇒ bar only.
    if (profileSaveEnabled(card)) {
      profileCompletionPromptedRef.current = true;
      setProfileSheetOpen(true);
    }
  }, [messages, profileCard, activeEntry]);

  // ── Target-price card replay on transcript load (Part 2, Task 12) ──
  // Twin of the thesis replay: runs only before any live card state exists.
  // A newer CARD-LESS (legacy) `target_price_saved` receipt kills the draft
  // inside replayCard → that session reopens with NO bar. A card-CARRYING
  // receipt (production shape) reopens the bar in its SAVED ✓ form — the
  // saved flag derives from the receipt itself below (savedReceiptHolds,
  // fix-wave 3), so the reloaded sheet's edits and save presses are gated
  // instead of 422ing / duplicating. Dismissal is deliberately ignored
  // by the domain replay (mirrors the backend: the agent keeps the card as
  // ground truth), so the UI scans for a `target_price_dismissed` receipt
  // newer than the winning snapshot itself — same "dismissed session reopens
  // bar-less" doctrine as thesis.
  useEffect(() => {
    if (targetPriceCard || targetPriceDismissedRef.current || sendingRef.current) return;
    // T16 single-slot doctrine (final-review FIX 2, thesis/profile twin): the
    // active artifact slot belongs to another kind — do not seed the
    // target-price bar/sheet over it. The draft registers no entries slot, so
    // this guard means "yield while any foreign artifact draft is active".
    if (activeEntry && activeEntry.kind !== 'target_price') return;
    let dismissed = false;
    for (let i = messages.length - 1; i >= 0; i--) {
      const meta = messages[i]?.metadata as Record<string, unknown> | null | undefined;
      if (!meta || typeof meta !== 'object') continue;
      if (meta.target_price_dismissed != null) {
        dismissed = true;
        break;
      }
      if (meta.target_price_card != null) break; // snapshot found first: dismissal is older
    }
    if (dismissed) {
      targetPriceDismissedRef.current = true;
      return;
    }
    // PR-review M2: project the computed panel BEFORE the card guard — a
    // saved session (receipt-carried card) rehydrates its panel even though
    // nothing else here may run (the sheet opens off the saved card below).
    // Task 9 panel projection (spec §3): the transcript's `target_price_computed`
    // artifact rehydrates the output panel on load — the compute round-trip's
    // state dies at reload otherwise. replayComputed returns null when the
    // newest draft-state row is a card/edit/saved receipt without a
    // fingerprint-matching card (the live path's stale rule + the M2 saved
    // arm), so a stale artifact degrades to exactly the pre-task behavior:
    // prices/weighted absent until the user recomputes.
    setTargetPriceComputed(replayTargetPriceComputed(messages));
    const card = replayTargetPriceCard(messages);
    if (!card) return;
    setTargetPriceCard(card);
    // Fix-wave 3 (PR #426 re-review): the saved flag derives from the
    // transcript, not only from the live save handler — when the latest
    // draft-state row is a card-carrying saved receipt this IS a saved
    // session: the bar renders its ✓ variant and the sheet's edit/save
    // handlers gate on the flag instead of POSTing into a 422 (edits) or a
    // duplicate receipt (save). Idempotent with the live-save path (both set
    // true); the SSE card path and the transcript-driven update effect clear
    // it whenever a NEWER snapshot re-arms a live draft.
    if (savedReceiptHolds(messages)) setTargetPriceSaved(true);
    appliedCardSeqRef.current = latestCardSnapshotSeq(messages);
    // 2026-10-31 doctrine: NO auto-open here — the sheet pops only from the
    // TP completion watcher below, when the §7 stamp fold is complete (all
    // 15 stage keys confirmed). A bare/mid-stage snapshot seeds the bar only.
  }, [messages, targetPriceCard, activeEntry]);

  // ── Transcript-driven card UPDATE (2026-10-16: pre-fill was refresh-only) ──
  // The drain-delivered seed turn (opener → 🌱 corrective turn) lands its
  // card ONLY in the transcript — the user's SSE stream closed before that
  // turn ran, so the SSE card path never fires and the seed effect above
  // skips (state non-null). On transcript refetch (WS push), a snapshot
  // NEWER than the last applied one live-updates the OPEN sheet — the same
  // replacement semantics as the SSE handler: changed values reset the
  // computed numbers, prune confirmations, un-save. No auto-open (the sheet
  // is already open); no clobber while the user's own stream is in flight
  // (the SSE path owns that window). The decision is the PURE
  // planCardLiveUpdate (unit-tested in targetPriceCardSeq.test.ts); this
  // effect only applies it. Dismissal is handled inside the planner: a
  // dismissed sheet re-arms ONLY for a snapshot newer than the persisted
  // dismissal receipt, so a dismissed card never reappears on refetch (L1).
  useEffect(() => {
    if (sendingRef.current) return;
    const plan = planCardLiveUpdate(messages, {
      appliedSeq: appliedCardSeqRef.current,
      prevCard: targetPriceCardRef.current,
      dismissed: targetPriceDismissedRef.current,
    });
    if (plan.kind === 'none') return;
    appliedCardSeqRef.current = plan.seq;
    if (plan.kind === 'echo') return; // identical re-emission: seq pinned, sheet untouched
    const prevCard = targetPriceCardRef.current;
    if (plan.clearDismissed) targetPriceDismissedRef.current = false; // L1 re-arm
    if (prevCard) setTargetPriceComputed(null);
    targetPriceCardRef.current = plan.card;
    setTargetPriceCard(plan.card);
    setTargetPriceConfirmed((prev) => pruneConfirmedForReplacement(prevCard, plan.card, prev));
    setTargetPriceSaved(false);
    targetPriceCompletionPromptedRef.current = false; // new draft lifecycle
  }, [messages]);

  // ── TP completion watcher (2026-10-31 doctrine) ──
  // The TP ACT-ON-FINAL state is the §7 stamp fold (all 15 stage keys
  // confirmed), which lives in the TRANSCRIPT, not the card — so the edge is
  // computed here, on the messages fold, exactly like the compute gate's
  // probe (PR #432's fence keys on the same fold). Covers both the replay
  // seed and the live path (confirm POSTs land stamps via refetch). Once per
  // draft lifecycle: the ref resets when the fold regresses, a saved draft
  // never pops (targetPriceSaved guard), and the T16 slot yield mirrors the
  // replay guard. A mid-stage fold (currentStageClient !== null) never pops —
  // the bar's fraction is the mid-build surface.
  const targetPriceConfirmedKeysRef = useRef(targetPriceConfirmedKeys);
  targetPriceConfirmedKeysRef.current = targetPriceConfirmedKeys;
  useEffect(() => {
    if (!targetPriceCard || targetPriceSaved || sendingRef.current) return;
    if (targetPriceDismissedRef.current) return;
    if (activeEntry && activeEntry.kind !== 'target_price') return;
    // An UNKNOWN fold is not a complete one (review #1): the server shipped no
    // `target_price_state`, so nothing here can say the build reached
    // ACT-ON-FINAL. Suppressing the pop is the safe side and costs nothing —
    // the bar stays as the mid-build surface, and the effect re-runs when the
    // fold lands. Popping on unknown would open the review sheet over a build
    // that may still be mid-stage, which is what the doctrine forbids.
    const fold = targetPriceConfirmedKeysRef.current;
    if (!fold || currentStageClient(fold) !== null) {
      targetPriceCompletionPromptedRef.current = false; // fold regressed / unknown
      return;
    }
    if (!targetPriceCompletionPromptedRef.current) {
      targetPriceCompletionPromptedRef.current = true;
      setTargetPriceSheetOpen(true);
    }
  }, [messages, targetPriceCard, targetPriceSaved, activeEntry]);

  // ── Import replay on transcript load (spec §4.5) ──
  // Seeds the bar/sheet ONCE from the loaded window (active ◔ → docked bar,
  // sheet stays closed until tapped; saved ✓ / undone ↩ receipts render from
  // the receipt itself). After the seed — or any live import event —
  // importLiveRef makes local state the sole owner of the lifecycle: WS
  // refetches cannot resurrect or clobber it, and transitions are applied by
  // the handlers below (the receipts are persisted server-side, so a reload
  // re-derives the same state through this replay).
  useEffect(() => {
    if (importLiveRef.current || messages.length === 0) return;
    const { preview, state } = replayImportSession(messages);
    if (state === 'none') return;
    importLiveRef.current = true;
    setImportState(state);
    setImportPreviewState(preview);
    if (state === 'saved') {
      // A replayed saved receipt still offers Undo: the receipt carries the
      // group_id postImportUndo needs.
      const gid = receiptGroupId(messages, 'import_saved');
      if (gid !== null) setImportSavedResults({ group_id: gid });
    }
  }, [messages]);

  // Bind the in-flight local URIs to the persisted import bubble: the newest
  // user message carrying import-command metadata after the send is ours.
  // Race guard: a WS/reconnect catch-up during the multi-second upload window
  // re-delivers the PREVIOUS import turn while the new message has not
  // persisted yet — binding to it would overwrite its URIs AND clear the
  // staging, leaving the new send a permanent placeholder. Skip when the
  // found message is already the last-bound one; the staged URIs survive
  // until the genuinely-new message lands.
  useEffect(() => {
    if (unboundImportUrisRef.current === null) return;
    const importMsg = [...messages].reverse().find((m) => m.role === 'user' && importCommandOf(m));
    if (!importMsg) return;
    if (lastBoundImportMsgIdRef.current === importMsg.id) return; // old turn — keep staging
    lastBoundImportMsgIdRef.current = importMsg.id;
    localUrisRef.current = {
      ...localUrisRef.current,
      [importMsg.id]: unboundImportUrisRef.current,
    };
    unboundImportUrisRef.current = null;
    forceUrisRender(); // the bubble rendered placeholder a frame ago — repaint
  }, [messages]);

  // Keep the across-await mirror current (see the ref's declaration).
  useEffect(() => {
    importPreviewRef.current = importPreviewState;
  }, [importPreviewState]);

  // ── Thesis sheet handlers ──
  // A save receipt finalizes the session's draft (thesis-edits would 422
  // server-side — replay stops at the receipt), so edits are blocked HERE
  // rather than posted and silently reverted on reload. Revisions happen via
  // the thesis form or a fresh Build session.
  // Otherwise optimistic first: the sheet must feel instant, and a failed POST
  // leaves the user's edit on screen rather than silently dropping it. The
  // server's coerced card replaces the local merge when the POST lands.
  const handleThesisEdit = useCallback(
    async (fields: Partial<ThesisCard>) => {
      if (!sessionId) return;
      if (thesisSaved) {
        showError(
          t('chat.thesisFinalized', {
            defaultValue:
              'Thesis saved — revise it on the thesis form, or start a new Build with TopNote session.',
          })
        );
        return;
      }
      setThesisCard((prev) => (prev ? ({ ...prev, ...fields } as ThesisCard) : prev));
      try {
        const { card } = await postThesisEdit(sessionId, fields as Record<string, unknown>);
        setThesisCard(card);
      } catch (e) {
        showError((e as Error).message);
      }
    },
    [sessionId, thesisSaved, t]
  );

  // Save (optionally to an explicit target). A 409 both-exists conflict means
  // the backend could not auto-resolve: ask which target receives the thesis
  // and retry with it (spec §3.6) instead of dead-ending on an error toast.
  const saveThesisTo = async (target?: ThesisTarget) => {
    if (!sessionId || !thesisCard) return;
    setSavingThesis(true);
    try {
      await postThesisSave(sessionId, thesisCard, target);
      setThesisSaved(true);
      setSheetOpen(false);
      showSuccess(t('chat.thesisSavedToast', { defaultValue: 'Thesis saved' }));
      // T16: land the symbol-bearing receipt without waiting for the WS push —
      // the return chips replay the transcript, so the displaced-draft set
      // must update even when the socket is slow.
      void refetchMessages();
    } catch (e) {
      if (e instanceof ThesisSaveConflictError && e.hasCandidates) {
        const posId = e.positionId;
        const wlId = e.watchlistItemId;
        const buttons: { text: string; style?: 'cancel'; onPress: () => void }[] = [];
        if (posId != null) {
          buttons.push({
            text: t('chat.thesisTargetPosition', { defaultValue: 'Position' }),
            onPress: () => saveThesisTo({ kind: 'position', id: posId }),
          });
        }
        if (wlId != null) {
          buttons.push({
            text: t('chat.thesisTargetWatchlist', { defaultValue: 'Watchlist' }),
            onPress: () => saveThesisTo({ kind: 'watchlist_item', id: wlId }),
          });
        }
        buttons.push({
          text: t('common.cancel', { defaultValue: 'Cancel' }),
          style: 'cancel',
          onPress: () => {},
        });
        Alert.alert(
          t('chat.thesisTargetPrompt', { defaultValue: 'Save this thesis to…' }),
          undefined,
          buttons
        );
        return;
      }
      showError((e as Error).message);
    } finally {
      setSavingThesis(false);
    }
  };

  // The sheet's save button: plain closure over saveThesisTo (cheap re-bind,
  // and the recursion inside saveThesisTo rules out a useCallback self-reference).
  const handleThesisSave = () => saveThesisTo();

  // ── Target-price sheet handlers (Part 2, Task 12) ──
  // Clone of handleThesisEdit WITH the snapshot rollback the thesis path lacks
  // (review D5): a failed edit POST restores the EXACT pre-edit card instead
  // of leaving the optimistic merge on screen — the card is what the user
  // believes their draft says, so a misstated draft is worse than a dropped
  // keystroke. Optimistic merge goes through the domain applyEdit (the same
  // coercion the backend runs), and the server's folded card replaces it.
  // An optional `confirmKey` (primary-driver row commit) is marked ONLY after
  // the POST lands — a confirmation must never survive a failed edit. The
  // ✓-only path (handleDriverConfirm, no POST) stays immediate.
  const handleTargetPriceEdit = useCallback(
    async (fields: Partial<TargetPriceCard>, confirmKey?: string) => {
      if (!sessionId) return;
      // A save receipt finalizes the draft (target-price-edits would 422
      // server-side — replay stops at the receipt), so block it HERE with an
      // explicit error rather than posting and silently reverting on reload.
      if (targetPriceSaved) {
        showError(
          t('chat.targetPriceFinalized', {
            defaultValue: 'Target price saved — start a new Build session to revise the draft.',
          })
        );
        return;
      }
      const snapshot = targetPriceCard;
      if (snapshot) setTargetPriceCard(applyTargetPriceEdit(snapshot, fields));
      try {
        // The accept list rides every edit POST: the server stamps those stage
        // keys target_price_accept and drains the staged advance — without it
        // the wizard stalls at its first input group forever (PR #421
        // criterion 2). PR-review fix B: the list is CHANGED-ONLY —
        // acceptKeysForEdit compares against the pre-edit card, so a sheet
        // commit never bulk-stamps the sibling drivers a wholesale dict patch
        // happens to carry; the one accept-unchanged path left is the sheet's
        // explicit confirm-all button. No card (nothing to diff against) →
        // no accepts; the server 422s this POST anyway. §7 provenance: the
        // companion `edited` list rides alongside — a sheet edit IS the
        // user's own typing, so it equals the accept list (T7 review minor);
        // the server aggregates provenance from the persisted stamps at save.
        const { card } = await postTargetPriceEdit(
          sessionId,
          fields as Record<string, unknown>,
          snapshot ? acceptKeysForEdit(fields, snapshot) : [],
          snapshot ? editedKeysForEdit(fields, snapshot) : []
        );
        setTargetPriceCard(card);
        // Engagement marking belongs to the SUCCESS path only (review #2): a
        // confirmation means "the user confirmed a value that IS the draft".
        // Marking before the POST armed the compute gate — and provenanceOf's
        // 'user' label in the §15 audit payload — for a value a failed edit
        // never persisted.
        if (confirmKey) {
          setTargetPriceConfirmed((prev) => ({ ...prev, [confirmKey]: true }));
        }
        // The draft moved: numbers computed from the old one are stale.
        setTargetPriceComputed(null);
      } catch (e) {
        setTargetPriceCard(snapshot); // restore — never leave a misstated draft
        showError((e as Error).message);
      }
    },
    [sessionId, targetPriceSaved, targetPriceCard, t]
  );

  // The sheet's onEdit: a pure pass-through — the value commit AND its
  // engagement marking both live in handleTargetPriceEdit, so the marking can
  // only happen on a persisted edit (review #2).
  const handleTargetPriceSheetEdit = useCallback(
    (fields: Partial<TargetPriceCard>, confirmKey?: string) => {
      void handleTargetPriceEdit(fields, confirmKey);
    },
    [handleTargetPriceEdit]
  );

  // ── Wizard input-group bubble (tp-wizard Task 11) ──
  // DISPLACEMENT (spec §2 Global Constraints): the group on the LATEST
  // assistant row carrying one is the live stage — every earlier group
  // renders read-only (the bubble shows no affordances for it at all).
  // PR-review fix K: inputGroupOf used to run twice per assistant message per
  // render (this list AND the render map below); ONE pass builds the list the
  // displacement index and the render dispatch both consume.
  const inputGroups = useMemo(
    () =>
      messages.flatMap((m, idx) => {
        const group = m.role === 'assistant' ? inputGroupOf(m) : null;
        return group ? [{ idx, group }] : [];
      }),
    [messages]
  );
  const latestGroupIndex =
    inputGroups.length > 0 ? inputGroups[inputGroups.length - 1].idx : -1;
  const inputGroupByIndex = useMemo(() => {
    const byIdx = new Map<number, TargetPriceInputGroupData>();
    for (const { idx, group } of inputGroups) byIdx.set(idx, group);
    return byIdx;
  }, [inputGroups]);

  // ── Research-staleness card (research-freshness spec §C.7) ──
  // Per-message parse results indexed once, the way fix K did for the input
  // group, so the render map never re-parses a card's metadata per frame.
  // Dismissed cards drop out here rather than at the render site: the
  // suppression is the transcript's, not the notice's, so the ✕ and a reload
  // cannot disagree about what is hidden.
  const dismissedStale = useMemo(() => refreshDismissals(messages), [messages]);
  const [staleHiddenHere, setStaleHiddenHere] = useState<Set<string>>(() => new Set());
  const staleCardByIndex = useMemo(() => {
    const byIdx = new Map<number, StaleCoverageCard>();
    messages.forEach((m, idx) => {
      if (m.role !== 'assistant') return;
      const card = staleCoverageOf(m);
      if (!card) return;
      const versionKey = staleHideKey(card.ticker, card.version);
      // §C.5: the annotated card's dismissal can be stamped at the version the
      // refresh STARTED from (the publish bumped it without advancing the
      // catalyst), so that receipt covers this card too — otherwise the next
      // turn re-offers what the user already dismissed.
      const fromVersion = card.refresh_attempted?.from_version ?? null;
      if (
        dismissedStale.has(versionKey) ||
        (fromVersion !== null && dismissedStale.has(staleHideKey(card.ticker, fromVersion))) ||
        staleHiddenHere.has(versionKey)
      ) {
        return;
      }
      byIdx.set(idx, card);
    });
    return byIdx;
  }, [messages, dismissedStale, staleHiddenHere]);

  // The FREE-INPUT commit handler: postTargetPriceEdit(fields, [key]) with
  // the TYPED value (this path's value is correct — it is what the user just
  // entered). Accept-on-null guard, client layer (final-review I2), reading
  // the POST-COMMIT card (fix-wave re-review — mirrors the server stamp
  // guard, which now reads its own post-apply card): accept gates on the
  // value the commit actually lands, so a commit that CLEARS the key's value
  // sends accept=[] (nothing to accept — a stamp is evidence a value IS on
  // the card), while a commit that FILLS a hole accepts in the same POST.
  // The server filter covers old clients.
  // §7 provenance (task 8): the key ALSO rides `edited` — the typed subset —
  // only when the commit CHANGED the value vs the live card (the rendered
  // suggestion's source): a fill or an override is an edit, a retype of the
  // same number is an accept-unchanged, a clear is neither. This POST's stamp
  // is what the server folds into `target_price_state.edited` — the typed
  // evidence travels in the transcript, so the single confirm's `edited` fold
  // is reload-safe.
  // §7 CARD-STATE RULE (the typed-row screen test caught this): the commit
  // applies its fields to the card state OPTIMISTICALLY (the sheet path's
  // rule) and adopts the server's folded card on success — the stage's ONE
  // confirm folds ALL rows' values from this state, so a commit that left it
  // stale would be silently REVERTED by the confirm POST's fields.
  const handleInputGroupCommit = useCallback(
    async (key: string, value: number | null) => {
      if (!sessionId || !targetPriceCard) return;
      const snapshot = targetPriceCard;
      const fields = tpFieldsForRows([{ key, value }], snapshot);
      setTargetPriceCard(applyTargetPriceEdit(snapshot, fields));
      try {
        const committed = tpCardValueAt(applyTargetPriceEdit(snapshot, fields), key);
        const changed = committed != null && committed !== tpCardValueAt(snapshot, key);
        const { card } = await postTargetPriceEdit(
          sessionId,
          fields,
          committed != null ? [key] : [],
          changed ? [key] : []
        );
        setTargetPriceCard(card);
        await refetchMessages();
      } catch (e) {
        setTargetPriceCard(snapshot); // restore — never leave a misstated draft
        showError((e as Error).message);
      }
    },
    [sessionId, targetPriceCard, refetchMessages]
  );

  // The sheet's confirm-all (PR-review fix B) is GONE (B3, 2026-10-31 staging
  // 0100.HK): it existed to stamp the rest of an incomplete wizard in one
  // visible act, but the incompleteness it recovered from was the client's own
  // 20-message fold disagreeing with the server's 200-row one. With the fold
  // server-authoritative the button had nothing left to recover, and a second
  // confirm affordance next to 「確認並計算」 only invited the redundant POST the
  // user reported.

  // The group's ONE confirm (spec §7 single confirm, task 8 — replaces the
  // per-row chips and the accept-all row): fields = ALL rows' committed
  // values (the LIVE card's — accept-unchanged leaves evidence, and a stale
  // group row must never revert a newer sheet edit), accept = the stage's
  // keys, edited = the stage's TYPED subset from the server's wizard fold
  // (target_price_state.edited — populated only by typed commits, never by
  // this button). ONE POST completes the stage; the
  // server's advance drain drives the next turn. The button is disabled
  // until every row holds a value; the usable filter re-checks against the
  // fresher card (a stale group row over a cleared live value must not
  // stamp a hole), and the server re-checks stamp-side.
  //
  // THE FLUSH FOLD (2026-10-28 staging, bear EBIT margin): the button first
  // commits any row the user typed into but never blurred, and reports those
  // values here. They MUST be folded explicitly — the commit's optimistic
  // setTargetPriceCard cannot be visible in this tick's `targetPriceCard`
  // closure, so reading state alone would POST the AI's suggestion over what
  // the user just entered, and `edited` would miss a key whose provenance
  // stamp has not been refetched yet. A flushed CLEAR (null) drops the key
  // from accept, mirroring the commit path's accept-on-null rule.
  const handleInputGroupConfirm = useCallback(
    async (keys: string[], flushed: FlushedRow[] = []) => {
      if (!sessionId || !targetPriceCard) return;
      try {
        const overrides = new Map(flushed.map((f) => [f.key, f.value]));
        const valueAt = (k: string) =>
          overrides.has(k)
            ? (overrides.get(k) as number | null)
            : tpCardValueAt(targetPriceCard, k);
        const usable = keys.filter((k) => valueAt(k) != null);
        if (!usable.length) return;
        // M4: the spent-button window. Set only once we know this press is
        // actually going to POST (an all-null stage returns above without a
        // request, and must not lock the button), and cleared after the
        // refetch so the transcript stamp takes over the gating.
        setConfirmingTargetPriceGroup(true);
        const fields = tpFieldsForRows(
          usable.map((k) => ({ key: k, value: valueAt(k) })),
          targetPriceCard
        );
        const typed = targetPriceEditedKeys;
        const edited = keys.filter((k) => usable.includes(k) && (typed.has(k) || overrides.has(k)));
        await postTargetPriceEdit(sessionId, fields, usable, edited);
        await refetchMessages();
      } catch (e) {
        showError((e as Error).message);
      } finally {
        setConfirmingTargetPriceGroup(false);
      }
    },
    [sessionId, targetPriceCard, targetPriceEditedKeys, refetchMessages]
  );

  // The ✓ affordance with no value change ("using my value"): marks the
  // driver confirmed client-only. Stays valid across agent re-emissions —
  // the mark is per driver key, and the gate must not force a full
  // re-confirmation every turn.
  const handleTargetPriceCompute = useCallback(
    async (flushed: FlushedRow[] = []) => {
      if (!sessionId || !targetPriceCard) return;
      setComputingTargetPrice(true);
      try {
        const res = await postTargetPriceCompute(
          sessionId,
          tpCardWithFlush(targetPriceCard, flushed)
        );
        setTargetPriceComputed(res);
      } catch (e) {
        // Toast the kernel's rejection (unbounded wacc, probability drift);
        // the previous computed result stays on screen rather than vanishing.
        showError((e as Error).message);
      } finally {
        setComputingTargetPrice(false);
      }
    },
    [sessionId, targetPriceCard]
  );

  // Save (optionally to an explicit target). Target resolution mirrors the
  // thesis flow: the client sends NO target by default and the backend
  // auto-resolves position/watchlist by the card's symbol; a 409 both-exists
  // conflict offers the same chooser and retries with the picked target.
  // Any other 4xx — above all the no-thesis hard-gate 422 — shows the server
  // detail inline (the message IS the explanation).
  const saveTargetPriceTo = async (target?: ThesisTarget, flushed: FlushedRow[] = []) => {
    if (!sessionId || !targetPriceCard || !targetPriceComputed) return;
    // Same finalization gate as the edit handler (fix-wave 3): once saved —
    // live or derived from the receipt on reload — a further Save press must
    // never append a duplicate `target_price_saved` receipt. Checked BEFORE
    // the computed guard: on a reloaded saved session the M2 panel projection
    // rehydrates `targetPriceComputed`, so saveEnabled would happily re-arm
    // the button. The conflict-chooser retry below re-enters with the flag
    // still false (the first POST 409ed), so the retry path is unaffected.
    if (targetPriceSaved) {
      showError(
        t('chat.targetPriceFinalized', {
          defaultValue: 'Target price saved — start a new Build session to revise the draft.',
        })
      );
      return;
    }
    const card = tpCardWithFlush(targetPriceCard, flushed);
    // §15 provenance: a flushed key is a USER value, but its commit POST has
    // not landed, so `targetPriceConfirmed` cannot know yet — marking it here
    // keeps the saved provenance from labelling the user's own number as the
    // AI's suggestion.
    const confirmed = { ...targetPriceConfirmed };
    flushed.forEach((f) => {
      if (f.value != null) confirmed[f.key] = true;
    });
    setSavingTargetPrice(true);
    try {
      await postTargetPriceSave(sessionId, {
        card,
        // NO computed in the save contract: the backend recomputes the kernel
        // fold server-side and persists OUR numbers (spec §12, T14). The
        // !targetPriceComputed guard above keeps the Save button gated until
        // the user has seen computed values — a UX signal only.
        provenance: provenanceOf(card, confirmed),
        ...(target ? { target } : {}),
      });
      setTargetPriceSaved(true);
      setTargetPriceSheetOpen(false);
      showSuccess(t('chat.targetPriceSavedToast', { defaultValue: 'Target price saved' }));
      void refetchMessages(); // land the receipt; reload replays it as closed
    } catch (e) {
      if (e instanceof ThesisSaveConflictError && e.hasCandidates) {
        const posId = e.positionId;
        const wlId = e.watchlistItemId;
        const buttons: { text: string; style?: 'cancel'; onPress: () => void }[] = [];
        if (posId != null) {
          buttons.push({
            text: t('chat.thesisTargetPosition', { defaultValue: 'Position' }),
            onPress: () => saveTargetPriceTo({ kind: 'position', id: posId }, flushed),
          });
        }
        if (wlId != null) {
          buttons.push({
            text: t('chat.thesisTargetWatchlist', { defaultValue: 'Watchlist' }),
            onPress: () => saveTargetPriceTo({ kind: 'watchlist_item', id: wlId }, flushed),
          });
        }
        buttons.push({
          text: t('common.cancel', { defaultValue: 'Cancel' }),
          style: 'cancel',
          onPress: () => {},
        });
        Alert.alert(
          t('chat.targetPriceTargetPrompt', { defaultValue: 'Save this target price to…' }),
          undefined,
          buttons
        );
        return;
      }
      showError((e as Error).message);
    } finally {
      setSavingTargetPrice(false);
    }
  };

  const handleTargetPriceSave = (flushed: FlushedRow[] = []) =>
    saveTargetPriceTo(undefined, flushed);

  // ── Profile save (revamp T24) ──
  // One REST call, no LLM: the backend writes one portfolio_theses version +
  // one user_personas version and appends the profile_saved receipt. Local
  // flip to the ✓ receipt happens immediately; the refetch lands the receipt
  // so the entries machinery stops re-arming the draft.
  const handleProfileSave = async () => {
    if (!sessionId || !profileCard) return;
    setSavingProfile(true);
    try {
      await postProfileSave(sessionId, profileCard);
      setProfileSaved(true);
      setProfileSheetOpen(false);
      showSuccess(t('chat.profileSavedToast', { defaultValue: 'Profile saved' }));
      void refetchMessages();
    } catch (e) {
      showError(
        (e as Error).message ||
          t('chat.profileSaveFailed', {
            defaultValue: 'Could not save the profile',
          })
      );
    } finally {
      setSavingProfile(false);
    }
  };

  // ── Profile sheet write-back (2026-10-31 spec §3.B) ──
  // Every chip tap is BOTH an edit and an accept of that dimension: the
  // patch lands locally (instant UI) and POSTs to /profile-edits, whose
  // server fold is the transcript's truth — the PROFILE CARD ground-truth
  // line reflects the user's writes next turn, killing the re-ask-decided-
  // dimensions bug. accept = keys whose landed value is non-null (the
  // server value-gates identically); edited = keys whose value changed.
  // Rollback restores the snapshot on failure — never leave a misstate.
  const handleProfileEdit = useCallback(
    (patch: Partial<ProfileCard>) => {
      if (!sessionId || !profileCard) return;
      // A save receipt finalizes the draft (profile-edits would 422
      // server-side — replay stops at the receipt), so block it HERE with an
      // explicit notice rather than posting and silently reverting on reload
      // (same gate as the target-price twin above).
      if (profileSaved) {
        showError(
          t('chat.profileFinalized', {
            defaultValue: 'Profile saved — start a new Build session to revise the draft.',
          })
        );
        return;
      }
      const snapshot = profileCard;
      setProfileCard((prev) => (prev ? { ...prev, ...patch } : prev));
      const accept = Object.entries(patch)
        .filter(([, v]) => v != null)
        .map(([k]) => k);
      const edited = Object.entries(patch)
        .filter(([k, v]) => v !== (snapshot as Record<string, unknown>)[k])
        .filter(([k]) => accept.includes(k))
        .map(([k]) => k);
      postProfileEdit(sessionId, patch as Record<string, unknown>, accept, edited)
        .then(({ card }) => setProfileCard(card))
        .catch(() => {
          setProfileCard(snapshot);
          showError(
            t('chat.profileEditFailed', { defaultValue: 'Could not save the edit' })
          );
        });
    },
    [sessionId, profileSaved, profileCard, t]
  );

  // Abort the SSE display pipe on unmount (session switch / screen exit).
  useEffect(() => () => abortRef.current?.abort(), []);

  // ── Send via streaming endpoint ──
  // The optional second arg carries the structured-turn fields (a build_thesis
  // seeding command, or the picked options of a multi question). The readable
  // `content` stays the optimistic bubble's label.
  const sendStreaming = useCallback(
    async (
      content: string,
      opts?: {
        command?:
          | ThesisCommand
          | ImportCommandPayload
          | DiscussItemCommandPayload
          | BuildProfileCommandPayload
          | TargetPriceCommandPayload
          | RefreshResearchCommandPayload;
        picked?: string[];
      }
    ) => {
      if (!sessionId) return;
      // Carry-forward B (T14 F1): structured sends during a live turn
      // (question chips, picked options, + menu, build_thesis) were silently
      // dropped by this gate. The typed-text path already queues in
      // handleSend, and the queued POST cannot carry a command — so anything
      // reaching sendStreaming while a turn is in flight is a chip / menu
      // tap. Toast instead of silently dropping it.
      if (sendingRef.current) {
        showError(
          t('chat.queuedBusy', {
            defaultValue: 'TopNote is still replying — try again when the current reply finishes',
          })
        );
        return;
      }
      sendingRef.current = true;
      // ── Persona-evolution interception (revamp T25) ──
      // A picked answer that is EXACTLY a knowledge level, on a question
      // whose prompt carries the agent's [persona] marker, is a confirmed
      // proposal: commit it through the dedicated endpoint (one persona
      // version + receipt, no LLM — the agent never writes the persona)
      // before the conversational turn proceeds; the receipt lands via the
      // post-turn refetch. Degrade safely: no marker, or a non-level answer,
      // is an ordinary chat turn.
      const personaLevel =
        questionRef.current?.prompt?.startsWith(PERSONA_QUESTION_MARKER) &&
        (KNOWLEDGE_LEVELS as readonly string[]).includes(content)
          ? (content as (typeof KNOWLEDGE_LEVELS)[number])
          : null;
      if (personaLevel) {
        try {
          await postPersonaConfirm(sessionId, personaLevel);
        } catch (err) {
          console.warn('[ChatScreen] persona confirm failed:', err);
          showError(
            t('chat.personaConfirmFailed', { defaultValue: 'Could not update the persona' })
          );
        }
      }
      setInputText('');
      setPendingMsg(content);
      setIsThinking(true);
      setStreamingText('');
      setProgress({ trail: [], total: 0 });
      setSuggestions([]);
      setQuestion(null);
      setStreamStartedAt(Date.now());

      try {
        const token = await getToken();
        if (!token) throw new Error('Not authenticated');

        const ac = new AbortController();
        abortRef.current = ac;
        await streamChatTurn({
          baseUrl: API_CONFIG.baseURL,
          token,
          sessionId,
          content,
          command: opts?.command,
          picked: opts?.picked,
          signal: ac.signal,
          onDelta: (delta) => {
            setStreamingText((prev) => (prev ?? '') + delta);
          },
          onToolCompleted: (tool, durationMs) => {
            // Accumulate, never clear: observed calls last 1–540ms, so an
            // in-flight indicator is on screen for under 1% of a turn.
            setProgress((prev) => ({
              trail: appendTrail(prev.trail, tool, durationMs),
              total: prev.total + 1,
            }));
          },
          onSuggestions: (s) => setSuggestions(sanitizeSuggestions(s)),
          onQuestion: (q) => {
            setQuestion(sanitizeQuestion(q));
            setSheetOpen(false); // a question takes the floor — auto-collapse (spec §3.1)
          },
          onThesisCard: (card) => {
            setThesisCard(card);
            setThesisSaved(false); // a re-emitted card is a new draft again
            thesisDismissedRef.current = false;
            // Completion transition → open the sheet as the save prompt: a
            // card that just became saveable (statement drafts last, so this
            // lands on the final builder turn) otherwise gives the user no
            // signal to save. Fires on the incomplete → saveEnabled edge,
            // once per completion — the ref resets on the incomplete branch,
            // so revise-then-recomplete re-prompts. (A re-emitted card just
            // cleared saved + dismissal two lines up: always an active
            // draft.) Opening an already-open sheet is a no-op. 2026-10-31
            // doctrine: the old first-event pop is gone — a mid-build card
            // seeds the bar only.
            if (saveEnabled(card)) {
              if (!completionPromptedRef.current) {
                completionPromptedRef.current = true;
                setSheetOpen(true);
              }
            } else {
              completionPromptedRef.current = false;
            }
          },
          onProfileCard: (card) => {
            setProfileCard(card);
            setProfileSaved(false); // a re-emitted card is a new draft again
            profileDismissedRef.current = false;
            // DOCTRINE: pop only at the ACT-ON-FINAL edge — all three
            // dimensions decided (the sheet is the review surface). Fires
            // once per draft; the ref resets on an incomplete re-emission,
            // so revise-then-recomplete re-prompts. A mid-build card seeds
            // the bar only.
            if (profileSaveEnabled(card)) {
              if (!profileCompletionPromptedRef.current) {
                profileCompletionPromptedRef.current = true;
                setProfileSheetOpen(true);
              }
            } else {
              profileCompletionPromptedRef.current = false;
            }
          },
          onTargetPriceCard: (card) => {
            // Target-price twin (Part 2 Task 12): the `target_price_card` SSE
            // event lands before the question chips (backend queue order), so
            // the sheet state arrives ahead of the cards' accept-or-edit chips.
            // A genuinely different re-emission invalidates the computed
            // numbers (they derive from the old draft); an identical echo of
            // the same card (the agent re-emits every builder turn) must not
            // clear a result the user just computed. The ref mirror exists
            // because sendStreaming must not depend on card state (same
            // doctrine as questionRef).
            const prevCard = targetPriceCardRef.current;
            if (JSON.stringify(prevCard) !== JSON.stringify(card)) {
              setTargetPriceComputed(null);
            }
            targetPriceCardRef.current = card;
            setTargetPriceCard(card);
            // Final-review FIX 3: an AGENT re-emission that CHANGED a driver
            // value must not keep the old value's confirmation — the gate
            // would arm for a number the user never confirmed and provenance
            // would mislabel it 'user'. Only agent-originated replacement
            // prunes: the edit-POST server card reflects the user's OWN edit,
            // so that path (commitTargetPriceEdit) keeps the map untouched.
            // pruneConfirmedForReplacement is value-diffing and returns the
            // same reference on an identical echo (no re-render churn).
            setTargetPriceConfirmed((prev) => pruneConfirmedForReplacement(prevCard, card, prev));
            setTargetPriceSaved(false); // a re-emitted card is a new draft again
            targetPriceDismissedRef.current = false;
            // 2026-10-31 doctrine: the live card event is BAR-ONLY — the
            // sheet pops from the TP completion watcher when the §7 stamp
            // fold completes (its activeEntry check carries the T16 yield
            // the old FIX-2 auto-open gate owned).
          },
          onImportPending: (count, importIds) => {
            // Extraction started: dock the reading row (with Cancel) and let
            // the compose stay usable — the turn keeps streaming under it.
            setImportPending({ count, importIds });
          },
          onImportPreview: (preview) => {
            importLiveRef.current = true;
            setImportPending(null); // extraction finished, review begins
            setImportState('active');
            setImportSavedResults(null);
            setImportPreviewState(preview);
            // Same-batch visibility (never visible=true against a stale/null
            // preview): auto-open the sheet once per mount, rows only — a
            // rowless preview has nothing to review and no bar either.
            if (preview.rows.length > 0 && !importAutoOpenedRef.current) {
              importAutoOpenedRef.current = true;
              setImportSheetOpen(true);
            }
          },
          onDone: async () => {
            setStreamingText(null);
            setProgress({ trail: [], total: 0 });
            setIsThinking(false);
            // A turn that ends without a preview (every row died, or the
            // user cancelled) must not strand the reading row.
            setImportPending(null);
            // Hold the recovery guard until the refetch lands — clearing it
            // before the messages update lets the orphan-detection effect
            // re-enable isThinking when the assistant row hasn't appeared yet.
            await refetchMessages();
            sendingRef.current = false;
          },
          onError: async (err) => {
            if (err instanceof RequestError && err.status === 428) {
              setStreamingText(null);
              setProgress({ trail: [], total: 0 });
              setPendingMsg(null);
              setIsThinking(false);
              setImportPending(null);
              await refetchConsent();
              sendingRef.current = false;
              return;
            }
            console.warn('[ChatScreen] stream error, falling back to polling:', err);
            setStreamingText(null);
            setProgress({ trail: [], total: 0 });
            setIsThinking(false);
            setImportPending(null);
            await refetchMessages();
            sendingRef.current = false;
          },
        });
      } catch (err) {
        console.warn('[ChatScreen] sendStreaming failed:', err);
        setStreamingText(null);
        setProgress({ trail: [], total: 0 });
        setIsThinking(false);
        setImportPending(null);
        await refetchMessages();
        sendingRef.current = false;
      }
    },
    [sessionId, refetchMessages, refetchConsent, t]
  );

  // ── Structured sends (the `+` command menu, or the thesis forms' entry) ──
  // The body carries the machine command; the optimistic bubble gets a
  // readable localized label. Commands are generic-only in the menu (no
  // client-side symbol inference, controller ruling 3); the form entry knows
  // its symbol and names it in the label.
  const sendStructured = useCallback(
    async (
      command:
        | ThesisCommand
        | ImportCommandPayload
        | BuildProfileCommandPayload
        | TargetPriceCommandPayload
        | RefreshResearchCommandPayload,
      opts?: { content?: string }
    ) => {
      if (command.command === 'build_thesis') {
        const base = t('chat.buildThesisLabel', { defaultValue: 'Build thesis' });
        const label = command.symbol ? `📐 ${base} — ${command.symbol}` : `📐 ${base}`;
        await sendStreaming(label, { command });
        return;
      }
      if (command.command === 'build_profile') {
        // Profile builder (revamp T24): the agent drives the chip flow; the
        // persisted bubble re-titles from the command metadata.
        await sendStreaming(
          `👤 ${t('chat.buildProfileLabel', { defaultValue: 'Build my profile' })}`,
          { command }
        );
        return;
      }
      if (command.command === 'target_price') {
        // Target-price builder (T15): the label always names the symbol —
        // every entry point resolves a concrete one BEFORE sending, because
        // the backend thesis hard-gate refuses a null-symbol command.
        const base = t('chat.targetPrice.build', { defaultValue: 'Build target price' });
        const label = command.symbol ? `🎯 ${base} — ${command.symbol}` : `🎯 ${base}`;
        await sendStreaming(label, { command });
        return;
      }
      if (command.command === 'refresh_research') {
        // The staleness card's confirm (research-freshness spec §C.4): the
        // ticker always names itself in the label — it is the required,
        // non-null half of the command, and the card's canonical spelling is
        // what the coverage row and the dismissal receipt both key on.
        const base = t('chat.staleCoverage.refresh', {
          defaultValue: 'Refresh the research',
        });
        await sendStreaming(`🔄 ${base} — ${command.ticker}`, { command });
        return;
      }
      // Import turn: the caption (or a bare 📷 when there is none) is the
      // readable label; the body carries the files + origin.
      await sendStreaming(opts?.content || '📷', { command });
    },
    [sendStreaming, t]
  );

  // The staleness notice's own button. Kept separate from the chip branch
  // because the card must stay actionable when the agent's question demotes
  // the chip row (§C.4a) — both route here, so the two affordances cannot
  // drift into two different turns.
  const handleStaleRefresh = useCallback(
    (ticker: string) => {
      void sendStructured({ command: 'refresh_research', ticker });
    },
    [sendStructured]
  );

  // The notice's ✕: hide at once (the tap must never look dead), then write
  // the version-scoped receipt and refetch so the transcript — not client
  // state — is what keeps it hidden on the next load.
  const dismissStaleCard = useCallback(
    async (card: StaleCoverageCard) => {
      setStaleHiddenHere((prev) => new Set(prev).add(staleHideKey(card.ticker, card.version)));
      // A version-less coverage row cannot carry a version-scoped receipt (the
      // endpoint's `version` is a required int), so the notice hides and no
      // 422 is fired into the console.
      if (!sessionId || typeof card.version !== 'number') return;
      try {
        await postRefreshDismiss(sessionId, card.ticker, card.version);
      } catch (e) {
        console.warn('[ChatConversation] refresh dismiss failed', e);
      }
      await refetchMessages();
    },
    [sessionId, refetchMessages]
  );

  // ── Target-price builder entry points (T15) ──
  // Every path sends sendStructured({command:'target_price', symbol}) with a
  // CONCRETE symbol — the backend thesis hard-gate turns a null-symbol send
  // into a refusal, so the UI resolves the symbol before dispatch.
  // Entry 1: the `+` menu swaps its popover into a position picker, fed by a
  // getPositions() fetch on tap (lightest existing pattern; the popover's
  // click-away doctrine covers it for free).
  const openTargetPricePicker = useCallback(async () => {
    setTpPickerOpen(true);
    setTpPickerLoading(true);
    setTpPickerSymbols(null);
    try {
      const positions = await getPositions();
      const symbols = Array.from(
        new Set(positions.map((p) => (p.symbol ?? '').trim()).filter((s) => s.length > 0))
      );
      setTpPickerSymbols(symbols);
    } catch {
      // A failed fetch reads as "no positions to pick from": the hint shows
      // and nothing is sent — a dead end beats an unguided refusal.
      setTpPickerSymbols([]);
    } finally {
      setTpPickerLoading(false);
    }
  }, []);

  // The shared dispatcher for all three entries: close the menu, send the
  // command naming the symbol. Guarded so a missing symbol can never reach
  // the wire (the hard constraint).
  const buildTargetPriceFor = useCallback(
    (symbol: string | null | undefined) => {
      const sym = (symbol ?? '').trim();
      if (!sym) return; // never send target_price without a concrete symbol
      setMenuOpen(false);
      setTpPickerOpen(false);
      // Stacking rule (spec review of the entries): an ACTIVE unsaved draft
      // for the SAME symbol is a resume, not a re-open — show the sheet
      // instead of burning a fresh command turn that would re-seed the
      // builder. A different symbol is a legitimate target switch and sends.
      // The comparison is normalized-to-normalized (review #6): card.symbol
      // is coerce_card's canonical form (upper, sliced), while the position
      // symbol arrives raw ('0700.hk'), so a raw compare missed the resume and
      // re-seeded the builder. The RAW symbol still goes on the wire — the
      // backend normalizes its side.
      if (
        !targetPriceSaved &&
        targetPriceCard &&
        targetPriceCard.symbol === normalizeCardSymbol(sym)
      ) {
        targetPriceDismissedRef.current = false;
        setTargetPriceSheetOpen(true);
        return;
      }
      void sendStructured({ command: 'target_price', symbol: sym });
    },
    [targetPriceCard, targetPriceSaved, sendStructured]
  );

  // ── Import picker + thumbnails row (spec §3.1 entry: `+` menu) ──
  // Mirrors ImportButton's gallery flow: permission → multi-select (max 10,
  // base64) → client-side 1..10 + 10MB/file enforcement (the backend 422s
  // otherwise). Canceled is a silent no-op; anything undockable toasts.
  const openImportPicker = useCallback(async () => {
    try {
      const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!permission.granted) {
        showError(
          t('portfolio.cameraRollPermission', {
            defaultValue: 'Camera roll permission is required',
          })
        );
        return;
      }
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        quality: 0.8,
        base64: true,
        allowsMultipleSelection: true,
        selectionLimit: IMPORT_MAX_FILES,
      });
      if (result.canceled) return; // closing the picker is not an error
      const assets = result.assets ?? [];
      if (assets.length === 0) {
        showError(t('chat.import.noneSelected', { defaultValue: 'No image selected' }));
        return;
      }
      if (assets.length > IMPORT_MAX_FILES) {
        showError(t('portfolio.maxImagesAllowed', { defaultValue: 'Maximum 10 images allowed' }));
        return;
      }
      const next: PendingImportFile[] = [];
      for (let i = 0; i < assets.length; i++) {
        const asset = assets[i];
        if (asset.fileSize && asset.fileSize > IMPORT_MAX_BYTES) {
          showError(
            t('portfolio.imageExceedsLimit', {
              defaultValue: 'Image "{{filename}}" exceeds 10MB limit',
              filename: asset.fileName || '',
            })
          );
          return; // reject the whole batch: the backend would 422 it anyway
        }
        if (!asset.base64) {
          showError(
            t('portfolio.failedToGetImageData', { defaultValue: 'Failed to get image data' })
          );
          return;
        }
        next.push({
          file_data: asset.base64,
          file_type: 'image',
          file_name: asset.fileName || `image_${i + 1}.jpg`,
          localUri: asset.uri,
        });
      }
      setPendingFiles(next);
    } catch (err) {
      console.warn('[ChatConversation] image picker failed:', err);
      showError(t('portfolio.failedToPickImages', { defaultValue: 'Failed to pick images' }));
    }
  }, [t]);

  const removePendingFile = useCallback((index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Send the docked thumbnails as one import turn. The base64 payloads go out
  // in the command; the local URIs are staged for the photo bubble and bound
  // to the persisted message once the refetch lands it.
  const sendImportFiles = useCallback(
    (caption: string) => {
      if (!sessionId || pendingFiles.length === 0) return;
      // Carry-forward B (T14 F1): never silently drop the docked thumbnails —
      // toast and leave them docked so the user can send after the turn ends.
      if (sendingRef.current) {
        showError(
          t('chat.queuedBusy', {
            defaultValue: 'TopNote is still replying — try again when the current reply finishes',
          })
        );
        return;
      }
      // Aggregate gate (hard gate, at send time): per-file limits do not bound
      // the SUM, and past the platform's 32MB request cap the send fails
      // opaquely. Abort BEFORE clearing the thumbnails so the user can trim
      // and retry.
      const totalBase64 = pendingFiles.reduce((n, f) => n + f.file_data.length, 0);
      if (totalBase64 > IMPORT_MAX_TOTAL_BASE64) {
        showError(
          t('chat.import.tooLarge', {
            defaultValue:
              'These screenshots are too large to send together — remove one or more and try again',
          })
        );
        return;
      }
      const files: ImportFilePayload[] = pendingFiles.map((f) => ({
        file_data: f.file_data,
        file_type: f.file_type,
        file_name: f.file_name,
      }));
      unboundImportUrisRef.current = pendingFiles.map((f) => f.localUri);
      setPendingFiles([]);
      void sendStructured(
        { command: 'import_positions', files, origin: 'menu' },
        { content: caption }
      );
    },
    [sessionId, pendingFiles, sendStructured, t]
  );

  // [Cancel] on the reading row: cancel every in-flight DocumentImport, then
  // persist the dismiss receipt (the backend cancel path deletes the rows,
  // but only the chat knows the session needed an import_discarded receipt
  // when no preview was ever persisted) and refresh so replay sees it.
  // Race guard: the `import_preview` SSE can land WHILE the cancels are in
  // flight — it re-arms the bar and auto-opens the sheet for rows this cancel
  // is about to dismiss. Once the dismiss resolves, any live preview over the
  // same import ids is dead: fold the local state to the receipt (close the
  // sheet, clear the bar) instead of leaving Apply pointed at a cancelled
  // group. A preview for a DIFFERENT group is untouched, and the receipt is
  // posted exactly once — the local fold does not re-dismiss.
  const handleImportPendingCancel = useCallback(async () => {
    const ids = importPending?.importIds ?? [];
    setImportPending(null);
    if (!sessionId || ids.length === 0) return;
    const results = await Promise.allSettled(ids.map((id) => cancelDocumentImport(id)));
    for (const r of results) {
      if (r.status === 'rejected')
        console.warn('[ChatConversation] import cancel failed', r.reason);
    }
    try {
      await postImportDismiss(sessionId, Math.min(...ids));
    } catch (e) {
      console.warn('[ChatConversation] import dismiss failed', e);
    }
    const live = importPreviewRef.current; // read at decision time, not render time
    if (live && live.import_ids.some((id) => ids.includes(id))) {
      setImportState('none');
      setImportPreviewState(null);
      setImportSavedResults(null);
      setImportSheetOpen(false);
    }
    await refetchMessages();
  }, [importPending, sessionId, refetchMessages]);

  // Terminal flip shared by every undo path: the bar goes ↩, the sheet closes.
  const flipImportUndone = useCallback(async () => {
    setImportState('undone');
    setImportSavedResults(null);
    setImportPreviewState(null);
    setImportSheetOpen(false);
    await refetchMessages();
  }, [refetchMessages]);

  // Bar's Undo: the bar has no API of its own (the sheet posts its own undo
  // and calls back through flipImportUndone via onUndone).
  const handleImportBarUndo = useCallback(async () => {
    const groupId = importSavedResults?.group_id ?? null;
    if (!sessionId || groupId === null) return;
    try {
      await postImportUndo(sessionId, groupId);
    } catch {
      showError(t('chat.import.undoFailed', { defaultValue: 'Undo failed — retry' }));
      return;
    }
    await flipImportUndone();
  }, [sessionId, importSavedResults, flipImportUndone, t]);

  // ✕ on the bar. On an ACTIVE preview this is a real dismissal: persist the
  // receipt (fire-and-forget — a failure only means the bar may re-arm on the
  // next reload, the local hide is never reverted) and refresh. On saved/undone
  // receipts it is a local clear only: the transcript stays the store.
  const dismissActiveImport = useCallback(
    async (groupId: number) => {
      if (!sessionId) return;
      setImportState('none');
      setImportPreviewState(null);
      setImportSavedResults(null);
      setImportSheetOpen(false);
      try {
        await postImportDismiss(sessionId, groupId);
      } catch (e) {
        console.warn('[ChatConversation] import dismiss failed', e);
      }
      await refetchMessages();
    },
    [sessionId, refetchMessages]
  );

  const handleImportBarDismiss = useCallback(() => {
    if (importState !== 'active' || importPreviewState === null) {
      setImportState('none');
      setImportPreviewState(null);
      setImportSavedResults(null);
      return;
    }
    void dismissActiveImport(importPreviewState.group_id);
  }, [importState, importPreviewState, dismissActiveImport]);

  // The sheet committed: the bar shows the ✓ receipt (with Undo), the sheet
  // STAYS OPEN on its Undo footer until the user closes it.
  const handleImportSaved = useCallback(
    (results: ImportSaveOutcome) => {
      importLiveRef.current = true;
      setImportState('saved');
      setImportPending(null);
      const gid = importPreviewState?.group_id ?? null;
      if (gid !== null) setImportSavedResults({ group_id: gid });
      void refetchMessages(); // land the receipt; a reload re-derives via replay
    },
    [importPreviewState, refetchMessages]
  );

  // Question-chip actions (spec §3.3): the import entry points and the
  // target-price refusal's thesis-builder opener an agent option can route to.
  // Actions never send a turn on their own label.
  const handleOptionAction = useCallback(
    (action: ImportOptionAction, symbol?: string | null) => {
      if (action === 'open_import_picker') {
        void openImportPicker();
        return;
      }
      if (action === 'open_import_sheet') {
        if (importPreviewState !== null) setImportSheetOpen(true);
        return;
      }
      if (action === 'discard_import') {
        if (importState === 'active' && importPreviewState !== null) {
          void dismissActiveImport(importPreviewState.group_id);
        }
        return;
      }
      if (action === 'open_thesis_builder') {
        // The target-price hard-gate's refusal chip: open the structured
        // build_thesis turn with the chip's symbol (key-omitted or null both
        // mean "no symbol" — the agent asks for one next turn).
        void sendStructured({ command: 'build_thesis', symbol: symbol ?? null });
        return;
      }
      if (action === 'refresh_research') {
        // The staleness card's confirm chip. Unlike build_thesis this command
        // requires a ticker — `RefreshResearchCommand.ticker` is a plain str,
        // so a symbol-less chip cannot send anything, and it must not degrade
        // into sending its own label as prose.
        if (symbol) void sendStructured({ command: 'refresh_research', ticker: symbol });
      }
    },
    [openImportPicker, importPreviewState, importState, dismissActiveImport, sendStructured]
  );

  // M5 (2026-10-31 staging, seq 12/13): a question option carrying the stage's
  // `accept` keys confirms the wizard stage instead of sending its label as
  // prose. The values are already on the card — the builder pre-fills before it
  // offers the chip — so this is the sheet confirm-all's POST shape exactly:
  // EMPTY fields, the chip's keys as accept, nothing typed. And NO chat turn:
  // the server's transition gate fires the advance turn, which IS the reply, so
  // one tap costs one turn instead of two.
  const handleQuestionAccept = useCallback(
    async (keys: string[]) => {
      if (!sessionId || !keys.length) return;
      // Never a silent tap (review #2). The chip sends NO chat turn — the
      // server's advance is the only reply — so every path that cannot produce
      // a stamp has to say so itself, or the user taps and the wizard simply
      // stalls with nothing on screen. Two such paths: the card snapshot has
      // not landed, and the stage's values are not on the card (the server's
      // `_accept_value_present` gate then drops every key, `accepted` comes
      // back empty and no advance fires). The prompt tells the model to write
      // the card before it offers the chip, but the prompt is the only guard —
      // and the bridge plugin exists precisely because the model does not
      // always write the card.
      if (!targetPriceCard) {
        showError(
          t('chat.targetPrice.chipCardNotReady', {
            defaultValue:
              "The target price draft hasn't loaded yet — tap the option again in a moment.",
          })
        );
        return;
      }
      if (targetPriceSaved) {
        showError(
          t('chat.targetPriceFinalized', {
            defaultValue: 'Target price saved — start a new Build session to revise the draft.',
          })
        );
        return;
      }
      // The server's stamp filter, mirrored: a key whose value is not on the
      // card cannot be accepted. Filtering here is what makes "nothing at all
      // is confirmable" detectable BEFORE the POST instead of discoverable
      // only as an empty response.
      const usable = keys.filter((k) => tpCardValueAt(targetPriceCard, k) != null);
      if (!usable.length) {
        showError(
          t('chat.targetPrice.chipNothingToConfirm', {
            defaultValue:
              'This step has no numbers to confirm yet — the draft card is empty here. Open the ' +
              'card and enter them, or ask the analyst for a set of values first.',
          })
        );
        return;
      }
      // The spent-button window (M4): the chip and the input group's Confirm
      // are the same act on the same stage, so the group's button goes spent
      // for the duration too — otherwise it re-arms and the user can double-post
      // through the other surface. The chip itself has no disabled affordance,
      // so the shared flag is also its re-entrancy guard.
      if (confirmingTargetPriceGroup) return;
      setConfirmingTargetPriceGroup(true);
      try {
        await postTargetPriceEdit(sessionId, {}, usable, []);
        await refetchMessages();
      } catch (e) {
        showError((e as Error).message);
      } finally {
        setConfirmingTargetPriceGroup(false);
      }
    },
    [sessionId, targetPriceCard, targetPriceSaved, confirmingTargetPriceGroup, refetchMessages, t]
  );

  // ── One-tap thesis resume (session reopen) ──
  // An unfinished, un-dismissed draft that is not currently asking anything
  // offers a single chip naming the NEXT builder step, derived from the
  // card's gaps in the fixed fill order (duration → pillars → risks →
  // statement → ready to save).
  const resumeNextStep = useMemo(() => {
    if (!thesisCard || thesisSaved) return null;
    if (thesisCard.duration == null)
      return t('chat.nextStrategy', { defaultValue: 'Next: Strategy' });
    if (thesisCard.pillars.length === 0)
      return t('chat.nextPillars', { defaultValue: 'Next: Pillars' });
    if (thesisCard.risks.length === 0) return t('chat.nextRisks', { defaultValue: 'Next: Risks' });
    if (!thesisCard.statement) return t('chat.nextStatement', { defaultValue: 'Next: Statement' });
    return t('chat.nextReview', { defaultValue: 'Ready to save' });
  }, [thesisCard, thesisSaved, t]);

  // Plain prose turn — no structured payload: the agent holds the card as
  // ground truth in its history (spec §4.2), so a normal message resumes it.
  const handleResumeThesis = useCallback(() => {
    if (!resumeNextStep) return;
    sendStreaming(
      `${t('chat.resumeThesisSend', { defaultValue: 'Continue thesis card' })} — ${resumeNextStep}`
    );
  }, [resumeNextStep, sendStreaming, t]);

  // ── Return-chip restore (T16) ──
  // Puts a displaced draft's snapshot back into the single bar+sheet slot —
  // LOCAL state only (the transcript stays the store; no message is written).
  // The one-shot refs ride along so the restored draft behaves like a live
  // one: the sheet opens; an already-complete card counts its save prompt as
  // delivered; a restored import marks the bar live so replays cannot clobber
  // it before the user acts.
  const handleRestoreArtifact = useCallback((entry: ArtifactEntry) => {
    setActiveOverride({ kind: entry.kind, target: entry.target });
    if (entry.kind === 'thesis' && entry.card) {
      setThesisCard(entry.card);
      setThesisSaved(false);
      setSheetOpen(true);
      completionPromptedRef.current = saveEnabled(entry.card);
    } else if (entry.kind === 'import' && entry.preview) {
      importLiveRef.current = true;
      setImportState('active');
      setImportPreviewState(entry.preview);
      setImportSavedResults(null);
      importAutoOpenedRef.current = true;
      setImportSheetOpen(true);
    } else if (entry.kind === 'profile' && entry.profileCard) {
      setProfileCard(entry.profileCard);
      setProfileSaved(false);
      profileDismissedRef.current = false;
      profileCompletionPromptedRef.current = profileSaveEnabled(entry.profileCard);
      setProfileSheetOpen(true);
    }
  }, []);

  // ── Initial message handling ──
  // Sits below sendStreaming/sendStructured so the deps arrays can reference
  // them directly (they are defined above, not hoisted). The one-shot
  // `autoSent` guard covers both flavors: a structured build_thesis command
  // (spec §3.2 entry A) takes precedence over a plain initialMessage.
  const prevInitialMessage = useRef<string | undefined>(undefined);
  const autoSent = useRef(false);
  // Pre-fill-once guard for the non-autoSend initialMessage path (see effect).
  const prefilled = useRef(false);

  // Consume-once cleanup: route params survive after the first send, and this
  // component remounts on every session switch (key=sessionId) with a fresh
  // autoSent guard — without clearing, returning to the session re-fires the
  // command and the agent re-presents the builder. Re-entry is via the + menu,
  // never an auto-refire.
  const clearInitialParams = useCallback(() => {
    navigation.setParams?.({
      initialCommand: undefined,
      initialMessage: undefined,
      autoSend: undefined,
    });
  }, [navigation]);

  // Reset auto-sent guard when a new initialMessage arrives
  if (initialMessage !== prevInitialMessage.current) {
    prevInitialMessage.current = initialMessage;
    autoSent.current = false;
  }

  useEffect(() => {
    if (!sessionId || autoSent.current) return;
    // Wait for the transcript to load before deciding: a returning session
    // already carries the command bubble from the original entry, and sending
    // on mount would re-trigger the builder.
    if (isLoading) return;
    if (initialCommand) {
      autoSent.current = true; // consumed either way — skip or send, never both
      if (!hasCommandBubble) {
        sendStructured(initialCommand);
      }
      clearInitialParams();
      return;
    }
    if (!initialMessage) return;
    if (autoSend) {
      autoSent.current = true;
      sendStreaming(initialMessage);
      clearInitialParams(); // same class of bug: a remount must not re-send
    } else if (!prefilled.current) {
      // Prefill exactly once: `messages` in the deps re-runs this effect on
      // every transcript refetch (WS catch-up, polling), and without this
      // guard a refetch would silently revert whatever the user edited into
      // the compose box after the prefill.
      prefilled.current = true;
      setInputText(initialMessage);
    }
  }, [
    sessionId,
    initialMessage,
    autoSend,
    initialCommand,
    isLoading,
    hasCommandBubble,
    sendStreaming,
    sendStructured,
    clearInitialParams,
  ]);

  // WebSocket: connect on mount, invalidate caches on server-push events
  useEffect(() => {
    if (!sessionId) return;
    let ws: TopnoteWS | null = null;
    let unsub: (() => void) | null = null;
    let unsubConnect: (() => void) | null = null;

    // Refetch the transcript + session list. Used both on push events and on
    // every (re)connect — the latter is the catch-up: a message delivered while
    // the socket was down (e.g. a run that finished during a long, dropped WS)
    // is otherwise never surfaced until a manual browser refresh.
    const catchUp = () => {
      refetchMessages();
      qc.invalidateQueries({ queryKey: ['chat-sessions'] });
    };

    (async () => {
      const token = await getToken();
      if (!token) return;
      ws = new TopnoteWS(token);
      unsub = ws.on((evt) => {
        if (
          evt.event === 'chat.message.appended' ||
          evt.event === 'run.completed' ||
          evt.event === 'run.failed' ||
          evt.event === 'research.completed' ||
          evt.event === 'research.failed'
        ) {
          // run.completed / run.failed are the truth-channel events for a normal
          // chat turn (run_flow emits them on success/failure). The SSE pipe only
          // carries live deltas + a 'done' marker; if it drops mid-turn (e.g. a
          // long, retrying overloaded turn), the WS is the reliable fallback that
          // surfaces the persisted reply — including the error message — live,
          // instead of leaving it to appear on the next manual refetch.
          catchUp();
        }
      });
      // Catch up on every (re)connect so events missed during a disconnect
      // gap (NOTIFY fan-out is fire-and-forget — no per-client durability) are
      // surfaced without a manual refresh.
      unsubConnect = ws.onConnect(catchUp);
      ws.connect();
    })();

    return () => {
      unsub?.();
      unsubConnect?.();
      ws?.close();
    };
  }, [sessionId, qc, refetchMessages]);

  // ── Auto-scroll ──
  // Jump to the end only when a NEW message lands. Loading older history
  // prepends to the top — that must NOT scroll (the viewport is held by
  // maintainVisibleContentPosition), so a bare messages-array dep won't do.
  const lastMsgId = messages.length > 0 ? messages[messages.length - 1].id : null;
  const prevLastMsgId = useRef<string | null>(null);
  useEffect(() => {
    if (!lastMsgId || lastMsgId === prevLastMsgId.current) return;
    prevLastMsgId.current = lastMsgId;
    requestAnimationFrame(() => {
      scrollRef.current?.scrollToEnd?.({ animated: true });
    });
  }, [lastMsgId]);

  // Follow the compose end-state: optimistic bubble, thinking indicator, and
  // the growing streamed text.
  useEffect(() => {
    if (!pendingMsg && !isThinking && !streamingText) return;
    requestAnimationFrame(() => {
      scrollRef.current?.scrollToEnd?.({ animated: true });
    });
  }, [pendingMsg, isThinking, streamingText]);

  // ── Queued send (spec §6.5) ──
  // A send while a turn streams: the non-stream POST returns
  // {queued: true, message} — the server persists the message and drains the
  // queue after the current turn. The optimistic local entry keeps the hint
  // count until the transcript carries the persisted copy (reconcile effect).
  const queueMessage = useCallback(
    async (content: string) => {
      if (!sessionId || queueingRef.current) return;
      queueingRef.current = true;
      setInputText('');
      const key = `queued-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setQueuedMsgs((prev) => [...prev, { key, content }]);
      try {
        const resp = await postMessage(sessionId, content);
        const persisted = 'queued' in resp ? resp.message : resp;
        // Rebind onto the persisted id — the transcript will carry it.
        setQueuedMsgs((prev) => prev.map((q) => (q.key === key ? { ...q, key: persisted.id } : q)));
      } catch (err) {
        console.warn('[ChatConversation] queued send failed:', err);
        setQueuedMsgs((prev) => prev.filter((q) => q.key !== key));
        // The message never persisted — give the text back to the compose box
        // so the user can retry without retyping it.
        setInputText(content);
        showError(t('chat.queuedSendFailed', { defaultValue: 'Could not queue the message' }));
      } finally {
        queueingRef.current = false;
      }
    },
    [sessionId, t]
  );

  // [停止] while a turn streams. Deliberately NOT gated on the {stopping}
  // response — the backend honestly answers stopping:false during the
  // create-window, and the press must work regardless. The SSE terminates
  // via the server; the partial reply lands via the stream/WS refetch.
  const handleStop = useCallback(() => {
    if (!sessionId) return;
    stopSession(sessionId).catch((err) => {
      console.warn('[ChatConversation] stop failed:', err);
    });
  }, [sessionId]);

  const handleSend = () => {
    if (!sessionId) return;
    setMenuOpen(false); // sending dismisses the popover — never send with it open
    // Docked thumbnails take the send: the caption is optional (bare 📷).
    if (pendingFiles.length > 0) {
      sendImportFiles(inputText.trim());
      return;
    }
    const text = inputText.trim();
    if (!text) return;
    // Deferred Feed discuss (revamp §6.3, revised 2026-10-09): the user's FIRST
    // typed message carries the held discuss_item command, so the item is
    // seeded server-side alongside their question instead of auto-firing on
    // mount. Latch + clear the route param so the card hides and later sends
    // are plain turns.
    if (showDiscussSeed && discussSeed) {
      // Gate BEFORE consuming the seed (mirrors sendImportFiles): a send while a
      // turn is in flight bails inside sendStreaming, so latching first would
      // strand the seed — the retry after the turn ends would then go out
      // WITHOUT the discuss context. Toast and leave the seed docked instead.
      if (sendingRef.current) {
        showError(
          t('chat.queuedBusy', {
            defaultValue: 'TopNote is still replying — try again when the current reply finishes',
          })
        );
        return;
      }
      setDiscussDone(true);
      navigation.setParams?.({ discussSeed: undefined });
      sendStreaming(text, { command: discussSeed.command });
      return;
    }
    // A turn is in flight (live stream or page-refresh recovery): queue the
    // message instead of dropping it — the server drains it after the turn.
    if (isThinking) {
      void queueMessage(text);
      return;
    }
    sendStreaming(text);
  };

  // ── Loading state ──
  if (isLoading) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <ScrollView
        ref={scrollRef}
        style={styles.messageList}
        contentContainerStyle={{ padding: 12, gap: 8 }}
        // Keep the user's viewport anchored when older pages are prepended
        // (native; ignored on web).
        maintainVisibleContentPosition={{ minIndexForVisible: 1 }}
      >
        {hasNextPage && (
          <Pressable
            onPress={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            accessibilityRole="button"
            accessibilityLabel={t('chat.loadMoreHistory', { defaultValue: 'Load more history' })}
            style={[
              styles.loadMoreBtn,
              { borderColor: theme.colors.border, backgroundColor: theme.colors.surface },
            ]}
          >
            {isFetchingNextPage ? (
              <ActivityIndicator size="small" color={theme.colors.primary} />
            ) : (
              <Text
                style={{
                  color: theme.colors.primary,
                  fontSize: theme.typography.fontSizeSm,
                  fontWeight: '600',
                }}
              >
                {t('chat.loadMoreHistory', { defaultValue: 'Load more history' })}
              </Text>
            )}
          </Pressable>
        )}
        {messages.map((msg, idx) => {
          // Import command turns render as photo bubbles (thumbnails while the
          // session is alive, 📷 placeholder after a reload) — MessageBubble
          // would collapse their metadata.command into a muted line.
          if (msg.role === 'user' && importCommandOf(msg)) {
            return (
              <ImportPhotoBubble key={msg.id} message={msg} uris={localUrisRef.current[msg.id]} />
            );
          }
          // Discuss_item turns (revamp §6.3) render as a compact titled
          // one-liner — the seed's title beats the generic command collapse.
          if (msg.role === 'user' && discussCommandOfMessage(msg)) {
            return <DiscussBubble key={msg.id} message={msg} />;
          }
          // Wizard input-group turns (tp-wizard Task 11, spec §7 single
          // confirm): the group bubble renders BENEATH the turn's markdown
          // bubble — the stage's assumption rows and the ONE 「確認」
          // button. Only the LATEST group is interactive (displacement); a
          // displaced group's ✓ / pending note comes from the stamp fold
          // below, never from displacement alone; the
          // confirm POSTs fields+accept+edited in ONE request + refetch, the
          // server's drain drives the advance. The group lookup reads the
          // memoized index map (fix K) — inputGroupOf no longer re-parses
          // per message per render.
          const group = msg.role === 'assistant' ? (inputGroupByIndex.get(idx) ?? null) : null;
          const staleCard = msg.role === 'assistant' ? (staleCardByIndex.get(idx) ?? null) : null;
          if (group || staleCard) {
            return (
              <View key={msg.id}>
                <MessageBubble message={msg} resolvedJobIds={resolvedJobIds} />
                {group ? (
                  <TargetPriceInputGroup
                    group={group}
                    editable={idx === latestGroupIndex}
                    card={targetPriceCard}
                    confirmedKeys={targetPriceConfirmedKeys}
                    // Only the live group can be mid-confirm; a displaced one is
                    // already read-only, so the flag needs no per-group identity.
                    confirming={confirmingTargetPriceGroup && idx === latestGroupIndex}
                    onConfirm={handleInputGroupConfirm}
                    onCommitRow={handleInputGroupCommit}
                  />
                ) : null}
                {staleCard ? (
                  <StaleCoverageNotice
                    card={staleCard}
                    onRefresh={handleStaleRefresh}
                    onDismiss={() => void dismissStaleCard(staleCard)}
                  />
                ) : null}
              </View>
            );
          }
          return <MessageBubble key={msg.id} message={msg} resolvedJobIds={resolvedJobIds} />;
        })}
        {pendingMsg && (
          <View
            style={[
              styles.bubble,
              { backgroundColor: theme.colors.primary, alignSelf: 'flex-end' },
            ]}
          >
            <Text
              style={{ color: theme.colors.textInverse, fontSize: theme.typography.fontSizeMd }}
            >
              {pendingMsg}
            </Text>
          </View>
        )}
        {importPending && (
          <View
            testID="import-pending-row"
            style={[
              styles.importPendingRow,
              {
                borderColor: theme.colors.border,
                backgroundColor: theme.colors.surfaceSecondary,
              },
            ]}
          >
            <Text
              style={{
                color: theme.colors.text,
                fontSize: theme.typography.fontSizeSm,
                flexShrink: 1,
              }}
            >
              ⣟ {t('chat.import.reading', { defaultValue: 'Reading your screenshot…' })}
            </Text>
            <Pressable
              testID="import-pending-cancel"
              onPress={() => void handleImportPendingCancel()}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              accessibilityRole="button"
              accessibilityLabel={t('common.cancel', { defaultValue: 'Cancel' })}
            >
              <Text
                style={{
                  color: theme.colors.primary,
                  fontSize: theme.typography.fontSizeSm,
                  fontWeight: '600',
                }}
              >
                [{t('common.cancel', { defaultValue: 'Cancel' })}]
              </Text>
            </Pressable>
          </View>
        )}
        {isThinking && (
          <ChatProgressPanel
            trail={progress.trail}
            total={progress.total}
            startedAt={streamStartedAt}
          />
        )}
        {streamingText && (
          // Two-track streaming (spec §3): the live turn is a de-emphasized
          // preview slice under the always-on panel, not a full markdown
          // bubble. The authoritative message arrives via refetch on done.
          <Text
            style={[
              styles.previewText,
              {
                color: theme.colors.textSecondary,
                fontSize: theme.typography.fontSizeXs,
                lineHeight: theme.typography.fontSizeXs * 1.5,
                marginHorizontal: theme.spacing.md,
                marginTop: theme.spacing.xs,
              },
            ]}
            numberOfLines={8}
            testID="chat-qa-preview"
          >
            {streamingText}
          </Text>
        )}
      </ScrollView>

      {/* Displaced unsaved drafts (T16): a chip per (kind,target) the single
          active bar slot does not own; tap restores its snapshot locally. */}
      {chipEntries.length > 0 && (
        <ReturnChips
          entries={chipEntries}
          onRestore={(chip) => {
            const full = entries.find((e) => e.kind === chip.kind && e.target === chip.target);
            if (full) handleRestoreArtifact(full);
          }}
        />
      )}

      {thesisBarVisible && (
        <ThesisDraftBar
          card={thesisCard}
          saved={thesisSaved}
          onOpen={() => setSheetOpen(true)}
          // T15 entry point 2: the thesis card's own builder opener, keyed to
          // the card's concrete symbol. Only offered on the DRAFT bar (the
          // saved receipt gets its follow-up chip below) and never while a
          // target-price draft owns the slot — no stacking, no null send.
          onBuildTargetPrice={
            thesisCard?.symbol && !thesisSaved && !targetPriceCard
              ? () => buildTargetPriceFor(thesisCard?.symbol)
              : undefined
          }
          onDismiss={() => {
            thesisDismissedRef.current = true;
            setThesisCard(null);
            // Persist the dismissal (system receipt in the transcript) so a
            // session reopen does not resurrect the bar. Fire-and-forget: the
            // local hide is never reverted; a failure only means the bar may
            // re-arm on the next reload.
            if (sessionId) {
              postThesisDismiss(sessionId).catch(() => {
                showError(
                  t('chat.thesisDismissFailed', {
                    defaultValue: 'Could not save the dismissal',
                  })
                );
              });
            }
          }}
        />
      )}

      {/* T15 entry point 3: the thesis-saved follow-up chip. Once a save
          lands for a symbol, the target-price builder is the natural next
          turn — offer it beside the ✓ receipt bar, keyed to the thesis's
          concrete symbol. Guarded by `!targetPriceCard` so it never stacks
          once a target-price draft takes over the slot. */}
      {thesisBarVisible && thesisSaved && thesisCard?.symbol && !targetPriceCard && (
        <View
          style={[
            styles.suggestionsRow,
            { borderColor: theme.colors.border, backgroundColor: theme.colors.surface },
          ]}
        >
          <Pressable
            testID="thesis-saved-build-tp"
            accessibilityRole="button"
            accessibilityLabel={`${t('chat.targetPrice.build', {
              defaultValue: 'Build target price',
            })} — ${thesisCard.symbol}`}
            onPress={() => buildTargetPriceFor(thesisCard?.symbol)}
            style={[styles.chip, { borderColor: theme.colors.border }]}
          >
            <Text
              style={{
                color: theme.colors.primary,
                fontSize: theme.typography.fontSizeSm,
                flexShrink: 1,
              }}
            >
              🎯 {t('chat.targetPrice.build', { defaultValue: 'Build target price' })} —{' '}
              {thesisCard.symbol}
            </Text>
          </Pressable>
        </View>
      )}

      {targetPriceBarVisible && targetPriceCard && (
        <TargetPriceDraftBar
          card={targetPriceCard}
          saved={targetPriceSaved}
          onOpen={() => setTargetPriceSheetOpen(true)}
          onDismiss={() => {
            targetPriceDismissedRef.current = true;
            targetPriceCardRef.current = null;
            setTargetPriceCard(null);
            setTargetPriceSheetOpen(false);
            // Persist the dismissal receipt (the transcript replay keeps the
            // card as the agent's ground truth; the marker is what keeps the
            // BAR dead across reloads). Fire-and-forget — the local hide is
            // never reverted, exactly the thesis-dismiss doctrine.
            if (sessionId) {
              postTargetPriceDismiss(sessionId).catch(() => {
                showError(
                  t('chat.targetPriceDismissFailed', {
                    defaultValue: 'Could not save the dismissal',
                  })
                );
              });
            }
          }}
        />
      )}

      {profileBarVisible && profileCard && (
        <ProfileDraftBar
          card={profileCard}
          saved={profileSaved}
          onOpen={() => setProfileSheetOpen(true)}
          onDismiss={() => {
            profileDismissedRef.current = true;
            setProfileCard(null);
            // Persist the dismissal (system receipt) so a session reopen does
            // not resurrect the bar (thesis/TP twin; the fix for the
            // un-killable profile draft). Fire-and-forget: the local hide is
            // never reverted; a failure only means the bar may re-arm on the
            // next reload.
            if (sessionId) {
              postProfileDismiss(sessionId).catch(() => {
                showError(
                  t('chat.profileDismissFailed', {
                    defaultValue: 'Could not save the dismissal',
                  })
                );
              });
            }
          }}
        />
      )}

      {/* Import review bar (spec §4.5): renders itself null for none/discarded
          and for an active state without rows; saved/undone render receipts. */}
      <ImportReviewBar
        state={importState === 'active' && !activeIsImport ? 'none' : importState}
        preview={importPreviewState}
        undoable={importSavedResults !== null}
        onOpen={() => {
          if (importPreviewState !== null) setImportSheetOpen(true);
        }}
        onDismiss={handleImportBarDismiss}
        onUndo={() => void handleImportBarUndo()}
      />

      {question && !isThinking && !streamingText ? (
        <QuestionChips
          // Remount on question swap so a previous question's multi-select
          // state can never leak into the next one. The FULL options list (not
          // just the first option) plus the multi flag: two questions sharing
          // a first option must not collide.
          key={`${question.prompt ?? ''}|${question.options
            .map((o) => (typeof o === 'string' ? o : o.label))
            .join('|')}|${question.multi === true ? 'multi' : 'single'}`}
          question={{ ...question, prompt: stripPersonaMarker(question.prompt) }}
          onPick={sendStreaming}
          onConfirmMulti={(picked) =>
            // The joined picks are the readable label (optimistic bubble);
            // the body carries the structured `picked` array.
            sendStreaming(picked.join(', '), { picked })
          }
          onOptionAction={handleOptionAction}
          onAccept={handleQuestionAccept}
        />
      ) : suggestions.length > 0 && !isThinking && !streamingText ? (
        <View
          style={[
            styles.suggestionsRow,
            { borderColor: theme.colors.border, backgroundColor: theme.colors.surface },
          ]}
        >
          <Text
            style={{
              color: theme.colors.textTertiary,
              fontSize: theme.typography.fontSizeXs,
              marginBottom: 4,
            }}
          >
            {t('chat.suggestionsPrompt', { defaultValue: 'You could ask:' })}
          </Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
            {suggestions.map((s, i) => (
              <Pressable
                key={i}
                onPress={() => sendStreaming(s)}
                style={[styles.chip, { borderColor: theme.colors.border }]}
              >
                <Text
                  style={{
                    color: theme.colors.primary,
                    fontSize: theme.typography.fontSizeSm,
                    flexShrink: 1,
                  }}
                >
                  {s}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>
      ) : resumeNextStep && !isThinking && !streamingText ? (
        // One-tap resume chip: only when the slot is otherwise empty (no
        // active question, no suggestions) and the draft is unfinished.
        <View
          style={[
            styles.suggestionsRow,
            { borderColor: theme.colors.border, backgroundColor: theme.colors.surface },
          ]}
        >
          <Pressable
            testID="thesis-resume-chip"
            onPress={handleResumeThesis}
            accessibilityRole="button"
            accessibilityLabel={`${t('chat.resumeThesis', { defaultValue: 'Continue thesis card' })} — ${resumeNextStep}`}
            style={[styles.chip, { borderColor: theme.colors.border }]}
          >
            <Text
              style={{
                color: theme.colors.primary,
                fontSize: theme.typography.fontSizeSm,
                flexShrink: 1,
              }}
            >
              {t('chat.resumeThesis', { defaultValue: 'Continue thesis card' })} — {resumeNextStep}
            </Text>
          </Pressable>
        </View>
      ) : null}

      {/* Dismiss surface for the `+` menu: covers everything above the
          compose row (it renders after those siblings) so any outside tap
          closes the menu without sending. */}
      {menuOpen && (
        <Pressable
          testID="compose-menu-backdrop"
          style={styles.menuBackdrop}
          onPress={() => setMenuOpen(false)}
        />
      )}

      {/* Docked picker output (spec §3.1 step 2): removable thumbnails above
          the compose row — rendered AFTER the menu backdrop so the ✕ buttons
          stay tappable while the menu is (transiently) open. */}
      {pendingFiles.length > 0 && (
        <View testID="import-pending-files" style={styles.thumbRow}>
          {pendingFiles.map((f, i) => (
            <View key={`${f.file_name}-${i}`} style={styles.thumbWrap}>
              <Image source={{ uri: f.localUri }} style={styles.thumb} />
              <Pressable
                testID={`import-pending-remove-${i}`}
                onPress={() => removePendingFile(i)}
                hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                accessibilityRole="button"
                accessibilityLabel={t('common.remove', { defaultValue: 'Remove' })}
                style={[
                  styles.thumbRemove,
                  {
                    backgroundColor: theme.colors.surface,
                    borderColor: theme.colors.border,
                  },
                ]}
              >
                <Text
                  style={{
                    color: theme.colors.textSecondary,
                    fontSize: theme.typography.fontSizeXs,
                  }}
                >
                  ✕
                </Text>
              </Pressable>
            </View>
          ))}
        </View>
      )}

      {/* Deferred Feed discuss (revamp §6.3, revised 2026-10-09): the picked
          item docks as a context card above the compose box until the user
          sends their first message — the opening turn is theirs, not auto-fired
          on mount. The command rides that first send (see handleSend). */}
      {showDiscussSeed && discussSeed && (
        <View
          testID="discuss-seed-card"
          style={[
            styles.discussSeedCard,
            { borderColor: theme.colors.border, backgroundColor: theme.colors.surface },
          ]}
        >
          <View style={styles.discussSeedHead}>
            <Text
              numberOfLines={1}
              style={{
                flex: 1,
                color: theme.colors.textSecondary,
                fontSize: theme.typography.fontSizeSm,
                fontWeight: '600',
              }}
            >
              {t('chat.discussingTitle', {
                title: discussSeed.title,
                defaultValue: '💬 Discussing: {{title}}',
              })}
            </Text>
            <Pressable
              testID="discuss-seed-dismiss"
              onPress={() => {
                // Dismiss without sending: hide the card now and clear the route
                // param so it does not resurrect on return (review B2).
                setDiscussDone(true);
                navigation.setParams?.({ discussSeed: undefined });
              }}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              accessibilityRole="button"
              accessibilityLabel={t('actions.dismiss', { defaultValue: 'Dismiss' })}
              style={styles.discussSeedDismiss}
            >
              <Text
                style={{ fontSize: theme.typography.fontSizeSm, color: theme.colors.textTertiary }}
              >
                ✕
              </Text>
            </Pressable>
          </View>
          {discussSeed.excerpt ? (
            <Text
              numberOfLines={3}
              style={{
                color: theme.colors.textTertiary,
                fontSize: theme.typography.fontSizeSm,
                marginTop: theme.spacing.xs,
              }}
            >
              {discussSeed.excerpt}
            </Text>
          ) : null}
          <Text
            style={{
              color: theme.colors.textTertiary,
              fontSize: theme.typography.fontSizeSm,
              marginTop: theme.spacing.xs,
            }}
          >
            {t('chat.discussSeedHint', {
              defaultValue: 'Type your question below — it sends with this item as context.',
            })}
          </Text>
        </View>
      )}

      {/* Queued sends (spec §6.5): messages sent while a turn streams, above
          the compose box — the count shrinks as the transcript catches up. */}
      {queuedMsgs.length > 0 && (
        <View
          testID="queued-hint"
          style={[
            styles.queuedHint,
            {
              borderColor: theme.colors.border,
              backgroundColor: theme.colors.surface,
            },
          ]}
        >
          <Text
            style={{
              color: theme.colors.textSecondary,
              fontSize: theme.typography.fontSizeSm,
            }}
          >
            {t('chat.queuedHint', { n: queuedMsgs.length, defaultValue: '{{n}} more queued' })}
          </Text>
        </View>
      )}

      <View
        testID="compose-row"
        style={[
          styles.composeRow,
          {
            borderColor: theme.colors.border,
            backgroundColor: theme.colors.surface,
          },
        ]}
        // Click-away for the `+` popover, part 2: the full-screen backdrop only
        // covers the area ABOVE this row (the row renders after it, so the
        // popover stays tappable). Taps on the row's own dead space run this
        // non-capturing check — returning false keeps normal touch routing, so
        // the input and + button are unaffected.
        onStartShouldSetResponder={() => {
          if (menuOpen) setMenuOpen(false);
          return false;
        }}
      >
        {/* `+` leads the row: a normal flex child LEFT of the input box (it
            used to be absolutely pinned inside the input's right edge, which
            read as a send-side affordance). The button and its popover share
            this anchor column, so the popover's left edge aligns with the
            glyph instead of landing one button-width into the input. */}
        <View testID="compose-menu-anchor">
          <Pressable
            testID="compose-menu"
            accessibilityRole="button"
            accessibilityLabel={t('chat.commandMenu', { defaultValue: 'Command menu' })}
            onPress={() => setMenuOpen((v) => !v)}
            style={styles.menuBtn}
          >
            <Ionicons name="add" size={22} color={theme.colors.primary} />
          </Pressable>
          {menuOpen && (
            <View
              testID="compose-menu-popover"
              style={[
                styles.menuPopover,
                {
                  borderColor: theme.colors.border,
                  backgroundColor: theme.colors.surface,
                  marginBottom: theme.spacing.xs,
                },
              ]}
            >
              {tpPickerOpen ? (
                // T15 entry 1: the popover swaps into the position picker —
                // a `target_price` command MUST carry a concrete symbol, so
                // the menu never sends blind. Loading → spinner; empty (or a
                // failed fetch) → the disabled no-positions hint.
                tpPickerLoading ? (
                  <View style={[styles.menuItem, styles.tpPickerCenter]}>
                    <ActivityIndicator size="small" color={theme.colors.primary} />
                  </View>
                ) : tpPickerSymbols && tpPickerSymbols.length > 0 ? (
                  <ScrollView style={styles.tpPickerList}>
                    {tpPickerSymbols.map((symbol) => (
                      <Pressable
                        key={symbol}
                        testID={`menu-target-price-symbol-${symbol}`}
                        accessibilityRole="button"
                        accessibilityLabel={`${t('chat.targetPrice.build', {
                          defaultValue: 'Build target price',
                        })} — ${symbol}`}
                        onPress={() => buildTargetPriceFor(symbol)}
                        style={styles.menuItem}
                      >
                        <Text
                          style={{
                            color: theme.colors.text,
                            fontSize: theme.typography.fontSizeSm,
                          }}
                        >
                          🎯 {symbol}
                        </Text>
                      </Pressable>
                    ))}
                  </ScrollView>
                ) : (
                  <View
                    testID="menu-target-price-empty"
                    style={[styles.menuItem, styles.tpPickerHint]}
                  >
                    <Text
                      style={{
                        color: theme.colors.textTertiary,
                        fontSize: theme.typography.fontSizeSm,
                      }}
                    >
                      {t('chat.targetPrice.noPositions', {
                        defaultValue: 'No positions yet — add one to build a target price',
                      })}
                    </Text>
                  </View>
                )
              ) : (
                <>
                  <Pressable
                    testID="menu-build-thesis"
                    accessibilityRole="button"
                    accessibilityLabel={t('chat.createThesis', { defaultValue: 'Create thesis' })}
                    onPress={() => {
                      setMenuOpen(false);
                      sendStructured({ command: 'build_thesis' });
                    }}
                    style={styles.menuItem}
                  >
                    <Text
                      style={{ color: theme.colors.text, fontSize: theme.typography.fontSizeSm }}
                    >
                      📐 {t('chat.createThesis', { defaultValue: 'Create thesis' })}
                    </Text>
                  </Pressable>
                  <Pressable
                    testID="menu-target-price"
                    accessibilityRole="button"
                    accessibilityLabel={t('chat.targetPrice.menuEntry', {
                      defaultValue: 'Build target price',
                    })}
                    onPress={() => {
                      void openTargetPricePicker();
                    }}
                    style={styles.menuItem}
                  >
                    <Text
                      style={{ color: theme.colors.text, fontSize: theme.typography.fontSizeSm }}
                    >
                      🎯 {t('chat.targetPrice.menuEntry', { defaultValue: 'Build target price' })}
                    </Text>
                  </Pressable>
                  <Pressable
                    testID="menu-import"
                    accessibilityRole="button"
                    accessibilityLabel={t('chat.menu.import', {
                      defaultValue: 'Import positions / trades',
                    })}
                    onPress={() => {
                      setMenuOpen(false);
                      void openImportPicker();
                    }}
                    style={styles.menuItem}
                  >
                    <Text
                      style={{ color: theme.colors.text, fontSize: theme.typography.fontSizeSm }}
                    >
                      📷 {t('chat.menu.import', { defaultValue: 'Import positions / trades' })}
                    </Text>
                  </Pressable>
                  <Pressable
                    testID="menu-build-profile"
                    accessibilityRole="button"
                    accessibilityLabel={t('chat.menu.profile', {
                      defaultValue: 'Build my profile',
                    })}
                    onPress={() => {
                      setMenuOpen(false);
                      sendStructured({ command: 'build_profile' });
                    }}
                    style={styles.menuItem}
                  >
                    <Text
                      style={{ color: theme.colors.text, fontSize: theme.typography.fontSizeSm }}
                    >
                      👤 {t('chat.menu.profile', { defaultValue: 'Build my profile' })}
                    </Text>
                  </Pressable>
                </>
              )}
            </View>
          )}
        </View>
        <View style={styles.inputWrapper}>
          <TextInput
            style={[styles.input, { color: theme.colors.text }]}
            value={inputText}
            onChangeText={setInputText}
            onFocus={() => setMenuOpen(false)} // typing means the popover isn't wanted
            placeholder={t('chat.placeholder', {
              defaultValue: 'Ask about a stock, sector, or market view...',
            })}
            placeholderTextColor={theme.colors.textSecondary}
            multiline
          />
        </View>
        {/* Stop affordance (spec §6.5): keyed off the client's own streaming
            state — while a turn runs, the send slot gains [停止]. */}
        {isThinking && (
          <Pressable
            testID="compose-stop"
            accessibilityRole="button"
            accessibilityLabel={t('chat.stop', { defaultValue: 'Stop' })}
            onPress={handleStop}
            style={styles.stopBtn}
          >
            <Text style={{ color: theme.colors.error, fontWeight: '600' }}>
              {t('chat.stop', { defaultValue: 'Stop' })}
            </Text>
          </Pressable>
        )}
        <Pressable
          testID="compose-send"
          onPress={handleSend}
          // Docked thumbnails make the send viable without a caption.
          disabled={!inputText.trim() && pendingFiles.length === 0}
          style={[
            styles.sendBtn,
            {
              opacity: !inputText.trim() && pendingFiles.length === 0 ? 0.5 : 1,
            },
          ]}
        >
          <Text style={{ color: theme.colors.primary, fontWeight: '600' }}>
            {t('chat.send', { defaultValue: 'Send' })}
          </Text>
        </Pressable>
      </View>

      <ThesisDraftSheet
        visible={sheetOpen && thesisCard !== null}
        card={thesisCard}
        onClose={() => setSheetOpen(false)}
        onEdit={handleThesisEdit}
        onSave={handleThesisSave}
        saving={savingThesis}
      />

      {/* Target-price sheet (Part 2, Task 12): per-field commits go through
          handleTargetPriceSheetEdit (optimistic applyEdit + rollback on a
          failed POST + confirmKey marking); the ✓ affordance confirms without
          a value change; Compute/Save are the engagement/compute-gated REST
          round-trips. confirmedKeys threads the SERVER's confirmed stage keys
          (target_price_state, B3) so Compute arms only on a fully confirmed
          wizard build. */}
      <TargetPriceDraftSheet
        visible={targetPriceSheetOpen && targetPriceCard !== null}
        card={targetPriceCard}
        confirmed={targetPriceConfirmed}
        confirmedKeys={targetPriceConfirmedKeys}
        computed={targetPriceComputed}
        onClose={() => setTargetPriceSheetOpen(false)}
        onEdit={handleTargetPriceSheetEdit}
        onCompute={(flushed) => void handleTargetPriceCompute(flushed)}
        onSave={handleTargetPriceSave}
        computing={computingTargetPrice}
        saving={savingTargetPrice}
      />

      {/* Profile sheet (revamp T24 + 2026-10-31 §3.B): every tap commits
          through /profile-edits (optimistic + rollback) — the transcript is
          the truth, the agent sees the writes next turn. Save stays the
          single version-writing REST call. */}
      <ProfileDraftSheet
        visible={profileSheetOpen && profileCard !== null}
        card={profileCard}
        onClose={() => setProfileSheetOpen(false)}
        onChange={handleProfileEdit}
        onSave={() => void handleProfileSave()}
        saving={savingProfile}
      />

      {/* Import review sheet (spec §3.2 step 5-7). Same-batch visibility: the
          flag and the preview only ever change in the same handler, so this
          never opens against a stale/null preview. On save the sheet STAYS
          open — its footer becomes [Undo] (savedResults) — until the user
          closes or undoes. Each server-folded edit round-trips back here so
          the in-memory preview never goes stale: a close→reopen re-seeds from
          the EDITED rows (deliberately unconditional — importLiveRef tracks
          WS/relay ownership of the draft, not local direct manipulation). */}
      <ImportReviewSheet
        visible={importSheetOpen && importPreviewState !== null}
        preview={importPreviewState}
        sessionId={sessionId}
        savedResults={importSavedResults}
        onClose={() => setImportSheetOpen(false)}
        onSaved={handleImportSaved}
        onUndone={() => void flipImportUndone()}
        onPreviewFolded={(folded) => setImportPreviewState(folded)}
      />
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, overflow: 'hidden' },
  messageList: { flex: 1 },
  bubble: {
    maxWidth: '80%',
    padding: 10,
    borderRadius: 12,
    marginVertical: 4,
    // Duplicated from MessageBubble's bubble, which drives persisted replies;
    // this one drives the pending bubble. Both need it or only half the
    // conversation clips to the portrait viewport.
    flexShrink: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  previewText: {
    // Layout only; spacing/color/size come from the theme inline at the render
    // site (repo rule: never hardcode spacing).
    maxWidth: '80%',
    alignSelf: 'flex-start',
    flexShrink: 1,
    minWidth: 0,
  },
  composeRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    padding: 8,
    borderTopWidth: 1,
  },
  input: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 15,
    maxHeight: 120,
  },
  // Wrapper that hosts the text box; the `+` button and its popover live in
  // the sibling anchor column (compose-menu-anchor), the row's first child.
  inputWrapper: { flex: 1 },
  menuBtn: { paddingHorizontal: 8, paddingVertical: 10 },
  // Transparent, screen-wide dismiss surface; rendered before the compose row
  // so the row (and its popover) paint above it.
  menuBackdrop: { ...StyleSheet.absoluteFillObject },
  // Anchored to the `+` button's column (left: 0 = the anchor column's left
  // edge, bottom: above the button) so the popover reads as attached to the
  // glyph (marginBottom comes from theme tokens inline). Absolute so opening
  // the menu never re-flows the compose row.
  menuPopover: {
    position: 'absolute',
    left: 0,
    bottom: '100%',
    minWidth: 180,
    borderWidth: 1,
    borderRadius: 12,
    paddingVertical: 4,
  },
  menuItem: { paddingHorizontal: 12, paddingVertical: 10 },
  // Target-price position picker (T15 entry 1): the popover swaps into a
  // scrollable symbol list; capped so a large portfolio cannot cover the
  // whole transcript.
  tpPickerList: { maxHeight: 240 },
  tpPickerCenter: { alignItems: 'center' },
  tpPickerHint: { opacity: 0.7 },
  sendBtn: { paddingHorizontal: 12, paddingVertical: 10 },
  stopBtn: { paddingHorizontal: 12, paddingVertical: 10 },
  // The queued-sends hint docked above the compose row.
  queuedHint: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderTopWidth: 1,
  },
  // Deferred Feed discuss context card (revamp §6.3, revised 2026-10-09).
  discussSeedCard: {
    marginHorizontal: 12,
    marginBottom: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  discussSeedHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 4,
  },
  discussSeedDismiss: {
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  // Docked import thumbnails (spec §3.1 step 2).
  thumbRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  thumbWrap: { width: 56, height: 56 },
  thumb: { width: 56, height: 56, borderRadius: 8 },
  thumbRemove: {
    position: 'absolute',
    top: -6,
    right: -6,
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // The `import_pending` reading row + Cancel (spec §3.1 step 4).
  importPendingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    maxWidth: '80%',
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 12,
    marginVertical: 4,
  },
  loadMoreBtn: {
    alignSelf: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 16,
    borderWidth: 1,
    marginVertical: 4,
  },
  suggestionsRow: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderTopWidth: 1,
  },
  chip: {
    // Same clipping fix as the chat bubbles below: without a width bound a
    // suggestion longer than the viewport overflows and is cut off at the
    // screen edge instead of wrapping onto multiple lines.
    maxWidth: '100%',
    flexShrink: 1,
    minWidth: 0,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 14,
    borderWidth: 1,
  },
});
